import { useEffect, useMemo, useRef, useState, Component } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ModelViewerClient from "@/components/ModelViewerClient";
import WebXRViewer from "@/components/WebXRViewer";
import { FaceARViewer, FaceAREditor } from "@/components/FaceAR";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getT } from "@/i18n/translations";

// Error Boundary for AR pages
class ARErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ARErrorBoundary] Error caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="webxr-fullscreen" style={{ background: "#111" }}>
          <div className="webxr-onboarding" style={{ textAlign: "center" }}>
            <h2 className="webxr-title" style={{ color: "#ef4444" }}>
              Wystąpił błąd uruchamiania AR
            </h2>
            <p className="webxr-subtitle" style={{ marginBottom: 16, color: "#999" }}>
              {this.state.error?.message || "Nieznany błąd"}
            </p>
            {this.state.errorInfo && (
              <pre style={{ 
                fontSize: 10, 
                color: "#666", 
                maxHeight: 100, 
                overflow: "auto",
                textAlign: "left",
                padding: 8,
                background: "#222",
                borderRadius: 8,
                marginBottom: 16
              }}>
                {this.state.errorInfo.componentStack?.slice(0, 500)}
              </pre>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button
                className="webxr-start-btn"
                onClick={() => window.location.reload()}
                style={{ padding: "12px 24px", background: "#3b82f6", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer" }}
              >
                Spróbuj ponownie
              </button>
              <button
                className="webxr-link"
                onClick={() => {
                  if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    window.location.href = "/";
                  }
                }}
                style={{ color: "#888" }}
              >
                Wróć do podglądu 3D
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WEBXR_ENABLED = process.env.REACT_APP_WEBXR_ENABLED === "true";

// Helper: Build Scene Viewer URL with validation
// Helper: Build Scene Viewer web URL
const buildSceneViewerWebUrl = (glbUrl, title, arPlacementType) => {
  if (!glbUrl || !glbUrl.startsWith("https://")) {
    console.error("[SCENE_VIEWER] Invalid GLB URL:", glbUrl);
    return null;
  }
  
  const fileUrl = encodeURIComponent(glbUrl);
  const encodedTitle = encodeURIComponent(title || "Model 3D");
  const verticalPlacement = arPlacementType === "wall" ? "&enable_vertical_placement=true" : "";
  
  return `https://arvr.google.com/scene-viewer/1.0?file=${fileUrl}&mode=ar_only&title=${encodedTitle}${verticalPlacement}`;
};

// Helper: Build Android Intent URL for Scene Viewer (PRIMARY on Android)
const buildSceneViewerIntentUrl = (glbUrl, title, arPlacementType, fallbackUrl) => {
  if (!glbUrl || !glbUrl.startsWith("https://")) {
    return null;
  }
  
  const fileUrl = encodeURIComponent(glbUrl);
  const encodedTitle = encodeURIComponent(title || "Model 3D");
  const verticalPlacement = arPlacementType === "wall" ? "&enable_vertical_placement=true" : "";
  
  // Web URL as browser fallback
  const webUrl = `https://arvr.google.com/scene-viewer/1.0?file=${fileUrl}&mode=ar_only&title=${encodedTitle}${verticalPlacement}`;
  
  // Intent URL - uses Google app (com.google.android.googlequicksearchbox) which includes Scene Viewer
  const intentUrl = 
    `intent://arvr.google.com/scene-viewer/1.0?file=${fileUrl}` +
    `&mode=ar_only&title=${encodedTitle}${verticalPlacement}` +
    `#Intent;scheme=https;package=com.google.android.googlequicksearchbox;` +
    `action=android.intent.action.VIEW;` +
    `S.browser_fallback_url=${encodeURIComponent(fallbackUrl || webUrl)};end;`;
  
  return intentUrl;
};

// Backward compatibility alias
const buildSceneViewerUrl = buildSceneViewerWebUrl;

// Helper: Open Scene Viewer - uses Intent on Android Chrome, web URL elsewhere
const openSceneViewer = (glbUrl, title, arPlacementType, projectId = null) => {
  console.info("[SCENE_VIEWER] Opening with params:", { glbUrl, title, arPlacementType, projectId });
  
  // Build web URL (fallback)
  const webUrl = buildSceneViewerWebUrl(glbUrl, title, arPlacementType);
  
  if (!webUrl) {
    console.error("[SCENE_VIEWER] Cannot build URL - invalid glbUrl");
    return;
  }

  // Detect Android Chrome
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/.test(ua);
  const isChrome = /Chrome/i.test(ua) && !/Edg|OPR|SamsungBrowser|UCBrowser/i.test(ua);
  
  console.info("[SCENE_VIEWER] Device detection:", { isAndroid, isChrome, ua: ua.substring(0, 100) });
  console.info("[SCENE_VIEWER] webUrl:", webUrl);

  if (isAndroid && isChrome) {
    // Android Chrome - use Intent URL (PRIMARY)
    const fallbackUrl = projectId 
      ? `${window.location.origin}/p/${projectId}` 
      : webUrl;
    const intentUrl = buildSceneViewerIntentUrl(glbUrl, title, arPlacementType, fallbackUrl);
    
    console.info("[SCENE_VIEWER] intentUrl:", intentUrl);
    console.info("[SCENE_VIEWER] Using Android Intent (primary)");
    
    window.location.href = intentUrl;
  } else {
    // Non-Android or non-Chrome - use web URL
    console.info("[SCENE_VIEWER] Using web URL (fallback/non-Android)");
    window.location.href = webUrl;
  }
};
const PRODUCTION_URL = process.env.REACT_APP_PRODUCTION_URL || "https://ar.arlens.pl";

const getToken = () => localStorage.getItem("studio_token");
const setTokenStorage = (token) => localStorage.setItem("studio_token", token);
const clearTokenStorage = () => localStorage.removeItem("studio_token");

const apiClient = axios.create({ baseURL: API });
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearTokenStorage();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

const formatDate = (value) =>
  new Date(value).toLocaleString("pl-PL", {
    dateStyle: "medium",
    timeStyle: "short",
  });

const formatDuration = (totalSeconds) => {
  if (totalSeconds == null) return "—";
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remaining = seconds % 60;
  if (hours > 0) {
    return `${hours} h ${minutes.toString().padStart(2, "0")} min`;
  }
  if (minutes > 0) {
    return `${minutes} min ${remaining.toString().padStart(2, "0")} s`;
  }
  return `${remaining} s`;
};

const useAuth = () => {
  const [token, setTokenState] = useState(getToken());
  const login = (newToken) => {
    setTokenStorage(newToken);
    setTokenState(newToken);
  };
  const logout = () => {
    clearTokenStorage();
    setTokenState(null);
  };
  return { token, login, logout };
};

const ProtectedRoute = ({ children, token }) => {
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const Shell = ({ children }) => (
  <div className="min-h-screen" data-testid="app-shell">
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10 lg:flex-row">
      {children}
    </div>
  </div>
);

const Sidebar = ({ onLogout }) => (
  <aside
    className="flex w-full flex-col gap-6 rounded-2xl border border-border/60 bg-white/90 p-6 shadow-[0_8px_24px_rgba(0,0,0,0.06)] lg:w-64"
    data-testid="admin-sidebar"
  >
    <div className="space-y-1" data-testid="admin-sidebar-brand">
      <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground" data-testid="admin-brand-label">
        Studio
      </p>
      <h2 className="text-2xl font-semibold" data-testid="admin-brand-title">
        Panel 3D
      </h2>
    </div>
    <nav className="flex flex-col gap-2" data-testid="admin-nav">
      <Link
        to="/dashboard"
        className="rounded-lg px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
        data-testid="nav-dashboard-link"
      >
        Projekty
      </Link>
      <Link
        to="/projects/new"
        className="rounded-lg px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
        data-testid="nav-new-project-link"
      >
        Nowy projekt
      </Link>
    </nav>
    <div className="mt-auto" data-testid="admin-sidebar-actions">
      <Button
        variant="outline"
        className="w-full"
        onClick={onLogout}
        data-testid="logout-button"
      >
        Wyloguj
      </Button>
    </div>
  </aside>
);

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post("/auth/login", { email, password });
      onLogin(response.data.access_token);
      toast.success("Zalogowano pomyślnie");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Nie udało się zalogować");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="login-page">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-6">
        <Card className="w-full max-w-lg" data-testid="login-card">
          <CardHeader>
            <CardTitle data-testid="login-title">Zaloguj do panelu</CardTitle>
            <CardDescription data-testid="login-description">
              Użyj danych admina skonfigurowanych w ENV.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit} data-testid="login-form">
              <div className="space-y-2" data-testid="login-email-group">
                <label className="text-sm font-medium" data-testid="login-email-label">
                  Email
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="admin@studio.local"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2" data-testid="login-password-group">
                <label className="text-sm font-medium" data-testid="login-password-label">
                  Hasło
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? "Logowanie..." : "Zaloguj"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInput, setDeleteInput] = useState("");

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get("/projects");
      setProjects(response.data);
    } catch (error) {
      toast.error("Nie udało się pobrać projektów");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleDeleteProject = async () => {
    if (!deleteTarget) return;
    if (deleteInput.trim() !== deleteTarget.name) {
      toast.error("Podaj nazwę projektu, aby potwierdzić usunięcie");
      return;
    }
    try {
      await apiClient.delete(`/projects/${deleteTarget.id}`);
      toast.success("Projekt usunięty");
      setProjects((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteInput("");
    } catch (error) {
      toast.error("Nie udało się usunąć projektu");
    }
  };

  return (
    <div className="flex w-full flex-1 flex-col gap-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4" data-testid="dashboard-header">
        <div>
          <h1 className="text-3xl font-semibold" data-testid="dashboard-title">
            Projekty 3D
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="dashboard-subtitle">
            Zarządzaj modelami, linkami publicznymi i kodami osadzenia.
          </p>
        </div>
        <Button asChild data-testid="dashboard-create-button">
          <Link to="/projects/new">Nowy projekt</Link>
        </Button>
        <Button asChild variant="outline" data-testid="dashboard-create-facear-button">
          <Link to="/projects/new-face-ar">
            <span className="flex items-center gap-2">
              Nowy Face AR <Badge variant="secondary" className="text-xs">beta</Badge>
            </span>
          </Link>
        </Button>
      </div>
      <div
        className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
        data-testid="dashboard-project-grid"
      >
        {loading && (
          <Card data-testid="dashboard-loading-card">
            <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="dashboard-loading-text">
              Ładowanie projektów...
            </CardContent>
          </Card>
        )}
        {!loading && projects.length === 0 && (
          <Card data-testid="dashboard-empty-card">
            <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="dashboard-empty-text">
              Brak projektów. Utwórz pierwszy model 3D.
            </CardContent>
          </Card>
        )}
        {projects.map((project) => {
          const previewUrl = `${window.location.origin}/p/${project.id}`;
          const isFaceAR = project.project_type === "face_ar";
          return (
          <Card
            key={project.id}
            className="flex flex-col justify-between transition hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
            data-testid={`project-card-${project.id}`}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle data-testid={`project-name-${project.id}`}>{project.name}</CardTitle>
                {isFaceAR && (
                  <Badge variant="secondary" className="text-xs">Face AR</Badge>
                )}
              </div>
              <CardDescription data-testid={`project-description-${project.id}`}>
                {project.description || "Brak opisu"}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-2" data-testid={`project-tags-${project.id}`}>
                <Badge variant="secondary" data-testid={`project-created-${project.id}`}>
                  {formatDate(project.created_at)}
                </Badge>
                <Badge variant="outline" data-testid={`project-glb-${project.id}`}>
                  GLB
                </Badge>
                {project.usdz_url && (
                  <Badge variant="outline" data-testid={`project-usdz-${project.id}`}>
                    USDZ
                  </Badge>
                )}
                {project.audio_url && (
                  <Badge variant="outline" data-testid={`project-audio-${project.id}`}>
                    Audio
                  </Badge>
                )}
                {isFaceAR && project.face_ar_product_type && (
                  <Badge variant="outline" className="capitalize">
                    {project.face_ar_product_type}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2" data-testid={`project-actions-${project.id}`}>
                <Button asChild variant="outline" size="sm" data-testid={`project-detail-${project.id}`}>
                  <Link to={`/projects/${project.id}`}>Szczegóły</Link>
                </Button>
                <Button asChild variant="outline" size="sm" data-testid={`project-edit-${project.id}`}>
                  <Link to={`/projects/${project.id}/edit`}>Edytuj</Link>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        setDeleteTarget(project);
                        setDeleteInput("");
                      }}
                      data-testid={`project-delete-${project.id}`}
                    >
                      Usuń
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="delete-project-modal">
                    <AlertDialogHeader>
                      <AlertDialogTitle data-testid="delete-project-title">Usunąć projekt?</AlertDialogTitle>
                      <AlertDialogDescription data-testid="delete-project-description">
                        Ta operacja jest nieodwracalna. Publiczny link przestanie działać.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2" data-testid="delete-project-input-group">
                      <label className="text-sm font-medium" data-testid="delete-project-input-label">
                        Wpisz nazwę projektu, aby potwierdzić
                      </label>
                      <Input
                        value={deleteInput}
                        onChange={(event) => setDeleteInput(event.target.value)}
                        placeholder={project.name}
                        data-testid="delete-project-input"
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-project-cancel">Anuluj</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground"
                        onClick={handleDeleteProject}
                        data-testid="delete-project-confirm"
                      >
                        Usuń projekt
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                
                {/* Conditional buttons based on project type */}
                {isFaceAR ? (
                  <>
                    <Button asChild variant="outline" size="sm" data-testid={`project-facear-editor-${project.id}`}>
                      <Link to={`/poc/face-ar-editor/${project.id}`}>Edytor Face AR</Link>
                    </Button>
                    <Button asChild size="sm" data-testid={`project-facear-preview-${project.id}`}>
                      <a href={`${window.location.origin}/poc/face-ar/${project.id}`} target="_blank" rel="noreferrer">
                        Podgląd Face AR
                      </a>
                    </Button>
                  </>
                ) : (
                  <Button asChild size="sm" data-testid={`project-preview-${project.id}`}>
                    <a href={previewUrl} target="_blank" rel="noreferrer">
                      Podgląd
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>
    </div>
  );
};

const NewProjectPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [glbFile, setGlbFile] = useState(null);
  const [usdzFile, setUsdzFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPlaybackMode, setAudioPlaybackMode] = useState("once");
  const [arPlacementType, setArPlacementType] = useState("floor");
  const [autoStartAr, setAutoStartAr] = useState(false);
  const [allowUserScaling, setAllowUserScaling] = useState(true);
  const [animationStartMode, setAnimationStartMode] = useState("auto");
  const [animationPlaybackMode, setAnimationPlaybackMode] = useState("loop");
  const [language, setLanguage] = useState("pl");
  const [loading, setLoading] = useState(false);

  const validateGlb = (file) => {
    if (!file) return "Plik GLB jest wymagany";
    if (!file.name.toLowerCase().endsWith(".glb")) return "Akceptujemy tylko pliki .glb";
    if (file.size >= 100 * 1024 * 1024) return "Plik GLB nie może przekraczać 100 MB";
    return null;
  };

  const validateOptional = (file, allowed) => {
    if (!file) return null;
    const ext = file.name.toLowerCase();
    if (!allowed.some((suffix) => ext.endsWith(suffix))) {
      return `Niepoprawny format: ${file.name}`;
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const glbError = validateGlb(glbFile);
    if (glbError) {
      toast.error(glbError);
      return;
    }
    const usdzError = validateOptional(usdzFile, [".usdz"]);
    if (usdzError) {
      toast.error(usdzError);
      return;
    }
    const audioError = validateOptional(audioFile, [".mp3", ".m4a"]);
    if (audioError) {
      toast.error(audioError);
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    formData.append("audio_playback_mode", audioPlaybackMode);
    formData.append("ar_placement_type", arPlacementType);
    formData.append("auto_start_ar", String(autoStartAr));
    formData.append("allow_user_scaling", String(allowUserScaling));
    formData.append("animation_start_mode", animationStartMode);
    formData.append("animation_playback_mode", animationPlaybackMode);
    formData.append("language", language);
    formData.append("glb", glbFile);
    if (usdzFile) formData.append("usdz", usdzFile);
    if (audioFile) formData.append("audio", audioFile);

    setLoading(true);
    try {
      const response = await apiClient.post("/projects", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Projekt utworzony");
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error("Nie udało się utworzyć projektu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full flex-1 flex-col gap-6" data-testid="new-project-page">
      <div data-testid="new-project-header">
        <h1 className="text-3xl font-semibold" data-testid="new-project-title">
          Nowy projekt
        </h1>
        <p className="text-sm text-muted-foreground" data-testid="new-project-subtitle">
          Dodaj model GLB, opcjonalny USDZ i audio do publicznego podglądu.
        </p>
      </div>
      <Card data-testid="new-project-card">
        <CardContent className="pt-6">
          <form className="space-y-6" onSubmit={handleSubmit} data-testid="new-project-form">
            <div className="space-y-2" data-testid="project-name-group">
              <label className="text-sm font-medium" data-testid="project-name-label">
                Nazwa projektu
              </label>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Sofa Cloud 2025"
                required
                data-testid="project-name-input"
              />
            </div>
            <div className="space-y-2" data-testid="project-description-group">
              <label className="text-sm font-medium" data-testid="project-description-label">
                Opis (opcjonalnie)
              </label>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Krótki opis produktu dla zespołu marketingu."
                rows={4}
                data-testid="project-description-input"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2" data-testid="project-files-grid">
              <div className="space-y-2" data-testid="project-glb-group">
                <label className="text-sm font-medium" data-testid="project-glb-label">
                  Plik GLB (max 100 MB)
                </label>
                <Input
                  type="file"
                  accept=".glb"
                  onChange={(event) => setGlbFile(event.target.files?.[0] || null)}
                  required
                  data-testid="project-glb-input"
                />
              </div>
              <div className="space-y-2" data-testid="project-usdz-group">
                <label className="text-sm font-medium" data-testid="project-usdz-label">
                  Plik USDZ (opcjonalnie)
                </label>
                <Input
                  type="file"
                  accept=".usdz"
                  onChange={(event) => setUsdzFile(event.target.files?.[0] || null)}
                  data-testid="project-usdz-input"
                />
              </div>
              <div className="space-y-2" data-testid="project-audio-group">
                <label className="text-sm font-medium" data-testid="project-audio-label">
                  Audio MP3/M4A (opcjonalnie)
                </label>
                <Input
                  type="file"
                  accept=".mp3,.m4a"
                  onChange={(event) => setAudioFile(event.target.files?.[0] || null)}
                  data-testid="project-audio-input"
                />
              </div>
              <div className="space-y-2" data-testid="project-audio-mode-group">
                <label className="text-sm font-medium" data-testid="project-audio-mode-label">
                  Tryb odtwarzania dźwięku
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={audioPlaybackMode}
                  onChange={(event) => setAudioPlaybackMode(event.target.value)}
                  disabled={!audioFile}
                  data-testid="project-audio-mode-select"
                >
                  <option value="once">Jednorazowo</option>
                  <option value="loop">W pętli</option>
                </select>
                {!audioFile && (
                  <p className="text-xs text-muted-foreground" data-testid="project-audio-mode-hint">
                    Tryb dostępny po dodaniu pliku audio.
                  </p>
                )}
              </div>
              <div className="space-y-2" data-testid="project-ar-placement-group">
                <label className="text-sm font-medium" data-testid="project-ar-placement-label">
                  Typ umieszczenia AR
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={arPlacementType}
                  onChange={(event) => setArPlacementType(event.target.value)}
                  data-testid="project-ar-placement-select"
                >
                  <option value="floor">Podłoga</option>
                  <option value="wall">Ściana</option>
                </select>
              </div>
              <div className="space-y-2" data-testid="project-auto-start-group">
                <label className="text-sm font-medium" data-testid="project-auto-start-label">
                  Automatycznie uruchom AR po załadowaniu
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={autoStartAr ? "true" : "false"}
                  onChange={(event) => setAutoStartAr(event.target.value === "true")}
                  data-testid="project-auto-start-select"
                >
                  <option value="false">Nie</option>
                  <option value="true">Tak</option>
                </select>
              </div>
              <div className="space-y-2" data-testid="project-allow-scaling-group">
                <label className="text-sm font-medium" data-testid="project-allow-scaling-label">
                  Pozwól użytkownikowi skalować model
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={allowUserScaling ? "true" : "false"}
                  onChange={(event) => setAllowUserScaling(event.target.value === "true")}
                  data-testid="project-allow-scaling-select"
                >
                  <option value="true">Tak (skala zostaje)</option>
                  <option value="false">Nie (podgląd, wraca do 100%)</option>
                </select>
              </div>
              <div className="space-y-2" data-testid="project-animation-start-group">
                <label className="text-sm font-medium" data-testid="project-animation-start-label">
                  Start animacji
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={animationStartMode}
                  onChange={(event) => setAnimationStartMode(event.target.value)}
                  data-testid="project-animation-start-select"
                >
                  <option value="auto">Automatycznie</option>
                  <option value="click">Po kliknięciu użytkownika</option>
                </select>
              </div>
              <div className="space-y-2" data-testid="project-animation-playback-group">
                <label className="text-sm font-medium" data-testid="project-animation-playback-label">
                  Tryb animacji
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={animationPlaybackMode}
                  onChange={(event) => setAnimationPlaybackMode(event.target.value)}
                  data-testid="project-animation-playback-select"
                >
                  <option value="once">Raz</option>
                  <option value="loop">W pętli</option>
                </select>
              </div>
              <div className="space-y-2" data-testid="project-language-group">
                <label className="text-sm font-medium" data-testid="project-language-label">
                  Język projektu
                </label>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={language}
                  onChange={(event) => setLanguage(event.target.value)}
                  data-testid="project-language-select"
                >
                  <option value="pl">Polski</option>
                  <option value="en">English</option>
                </select>
                <p className="text-xs text-muted-foreground" data-testid="project-language-hint">
                  Język tekstów widocznych dla użytkownika końcowego (strona projektu, AR, instrukcje).
                </p>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="new-project-submit">
              {loading ? "Zapisywanie..." : "Utwórz projekt"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const EditProjectPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [glbFile, setGlbFile] = useState(null);
  const [usdzFile, setUsdzFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [removeUsdz, setRemoveUsdz] = useState(false);
  const [removeAudio, setRemoveAudio] = useState(false);
  const [audioPlaybackMode, setAudioPlaybackMode] = useState("once");
  const [arPlacementType, setArPlacementType] = useState("floor");
  const [autoStartAr, setAutoStartAr] = useState(false);
  const [allowUserScaling, setAllowUserScaling] = useState(true);
  const [animationStartMode, setAnimationStartMode] = useState("auto");
  const [animationPlaybackMode, setAnimationPlaybackMode] = useState("loop");
  const [language, setLanguage] = useState("pl");
  const [initialCameraOrbit, setInitialCameraOrbit] = useState("");
  const [showOrbitModal, setShowOrbitModal] = useState(false);
  const orbitViewerRef = useRef(null);

  useEffect(() => {
    const loadProject = async () => {
      try {
        const response = await apiClient.get(`/projects/${id}`);
        setProject(response.data);
        setName(response.data.name || "");
        setDescription(response.data.description || "");
        setAudioPlaybackMode(response.data.audio_playback_mode || "once");
        setArPlacementType(response.data.ar_placement_type || "floor");
        setAutoStartAr(Boolean(response.data.auto_start_ar));
        setAllowUserScaling(response.data.allow_user_scaling !== false);
        setAnimationStartMode(response.data.animation_start_mode || "auto");
        setAnimationPlaybackMode(response.data.animation_playback_mode || "loop");
        setLanguage(response.data.language || "pl");
        setInitialCameraOrbit(response.data.initial_camera_orbit || "");
      } catch (error) {
        toast.error("Nie udało się pobrać projektu");
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [id]);

  useEffect(() => {
    console.log("[EDIT_PROJECT] project", project);
    console.log("[EDIT_PROJECT] initial orbit", project?.initial_camera_orbit);
  }, [project]);

  const validateOptional = (file, allowed) => {
    if (!file) return null;
    const ext = file.name.toLowerCase();
    if (!allowed.some((suffix) => ext.endsWith(suffix))) {
      return `Niepoprawny format: ${file.name}`;
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error("Nazwa projektu jest wymagana");
      return;
    }
    if (glbFile) {
      if (!glbFile.name.toLowerCase().endsWith(".glb")) {
        toast.error("Akceptujemy tylko pliki .glb");
        return;
      }
      if (glbFile.size >= 100 * 1024 * 1024) {
        toast.error("Plik GLB nie może przekraczać 100 MB");
        return;
      }
    }
    const usdzError = validateOptional(usdzFile, [".usdz"]);
    if (usdzError) {
      toast.error(usdzError);
      return;
    }
    const audioError = validateOptional(audioFile, [".mp3", ".m4a"]);
    if (audioError) {
      toast.error(audioError);
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    formData.append("audio_playback_mode", audioPlaybackMode);
    formData.append("ar_placement_type", arPlacementType);
    formData.append("auto_start_ar", String(autoStartAr));
    formData.append("allow_user_scaling", String(allowUserScaling));
    formData.append("animation_start_mode", animationStartMode);
    formData.append("animation_playback_mode", animationPlaybackMode);
    formData.append("language", language);
    if (glbFile) formData.append("glb", glbFile);
    if (usdzFile) formData.append("usdz", usdzFile);
    if (audioFile) formData.append("audio", audioFile);
    if (!usdzFile) formData.append("remove_usdz", String(removeUsdz));
    if (!audioFile) formData.append("remove_audio", String(removeAudio));

    setSaving(true);
    try {
      const response = await apiClient.put(`/projects/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Projekt zaktualizowany");
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error("Nie udało się zapisać zmian");
    } finally {
      setSaving(false);
    }
  };

  const serializeOrbit = (orbit) => {
    if (!orbit) return "";
    const theta = Number(orbit.theta).toFixed(4);
    const phi = Number(orbit.phi).toFixed(4);
    const radius = Number(orbit.radius).toFixed(4);
    return `${theta}rad ${phi}rad ${radius}m`;
  };

  const updateOrbitValue = async (orbitValue) => {
    if (!id) return;
    const formData = new FormData();
    formData.append("initial_camera_orbit", orbitValue || "");
    try {
      const response = await apiClient.put(`/projects/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProject(response.data);
      setInitialCameraOrbit(response.data.initial_camera_orbit || "");
      toast.success("Kadr startowy zapisany");
    } catch (error) {
      toast.error("Nie udało się zapisać kadru startowego");
    }
  };

  const handleSaveOrbit = async () => {
    const viewer = orbitViewerRef.current;
    if (!viewer) return;
    const orbitObj = viewer.getCameraOrbit ? viewer.getCameraOrbit() : null;
    const orbitValue = orbitObj ? serializeOrbit(orbitObj) : String(viewer.cameraOrbit || "");
    console.info("[START_VIEW_3D] saved orbit", orbitValue);
    await updateOrbitValue(orbitValue);
    setShowOrbitModal(false);
  };

  const handleResetOrbit = async () => {
    console.info("[START_VIEW_3D] reset orbit");
    await updateOrbitValue("");
  };

  if (loading) {
    return (
      <Card data-testid="edit-project-loading">
        <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="edit-project-loading-text">
          Ładowanie projektu...
        </CardContent>
      </Card>
    );
  }

  if (!project) {
    return (
      <Card data-testid="edit-project-empty">
        <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="edit-project-empty-text">
          Nie znaleziono projektu.
        </CardContent>
      </Card>
    );
  }

  const glbName = project.glb_url?.split("/").pop();
  const usdzName = project.usdz_url?.split("/").pop();
  const audioName = project.audio_url?.split("/").pop();

  return (
    <div className="flex w-full flex-1 flex-col gap-6" data-testid="edit-project-page">
      <div data-testid="edit-project-header">
        <h1 className="text-3xl font-semibold" data-testid="edit-project-title">
          Edytuj projekt
        </h1>
        <p className="text-sm text-muted-foreground" data-testid="edit-project-subtitle">
          Zaktualizuj dane i pliki bez zmiany publicznego linku.
        </p>
      </div>
      <Card data-testid="edit-project-card">
        <CardContent className="pt-6">
          <form className="space-y-6" onSubmit={handleSubmit} data-testid="edit-project-form">
            <div className="space-y-2" data-testid="edit-project-name-group">
              <label className="text-sm font-medium" data-testid="edit-project-name-label">
                Nazwa projektu
              </label>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                data-testid="edit-project-name-input"
              />
            </div>
            <div className="space-y-2" data-testid="edit-project-description-group">
              <label className="text-sm font-medium" data-testid="edit-project-description-label">
                Opis
              </label>
              <Textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={4}
                data-testid="edit-project-description-input"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2" data-testid="edit-project-files-grid">
              <div className="space-y-2" data-testid="edit-project-glb-group">
                <label className="text-sm font-medium" data-testid="edit-project-glb-label">
                  Plik GLB (opcjonalna podmiana)
                </label>
                {glbName && (
                  <p className="text-xs text-muted-foreground" data-testid="edit-project-glb-current">
                    Aktualny: {glbName}
                  </p>
                )}
                <Input
                  type="file"
                  accept=".glb"
                  onChange={(event) => setGlbFile(event.target.files?.[0] || null)}
                  data-testid="edit-project-glb-input"
                />
              </div>
              <div className="space-y-2" data-testid="edit-project-usdz-group">
                <label className="text-sm font-medium" data-testid="edit-project-usdz-label">
                  Plik USDZ
                </label>
                {usdzName ? (
                  <p className="text-xs text-muted-foreground" data-testid="edit-project-usdz-current">
                    Aktualny: {usdzName}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="edit-project-usdz-none">
                    Brak pliku USDZ
                  </p>
                )}
                <Input
                  type="file"
                  accept=".usdz"
                  onChange={(event) => {
                    setUsdzFile(event.target.files?.[0] || null);
                    setRemoveUsdz(false);
                  }}
                  data-testid="edit-project-usdz-input"
                />
                {usdzName && (
                  <label className="flex items-center gap-2 text-xs" data-testid="edit-project-usdz-remove">
                    <input
                      type="checkbox"
                      checked={removeUsdz}
                      onChange={(event) => setRemoveUsdz(event.target.checked)}
                      data-testid="edit-project-usdz-remove-checkbox"
                    />
                    Usuń USDZ
                  </label>
                )}
              </div>
              <div className="space-y-2" data-testid="edit-project-audio-group">
                <label className="text-sm font-medium" data-testid="edit-project-audio-label">
                  Audio MP3/M4A
                </label>
                {audioName ? (
                  <p className="text-xs text-muted-foreground" data-testid="edit-project-audio-current">
                    Aktualny: {audioName}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="edit-project-audio-none">
                    Brak pliku audio
                  </p>
                )}
                <Input
                  type="file"
                  accept=".mp3,.m4a"
                  onChange={(event) => {
                    setAudioFile(event.target.files?.[0] || null);
                    setRemoveAudio(false);
                  }}
                  data-testid="edit-project-audio-input"
                />
                {audioName && (
                  <label className="flex items-center gap-2 text-xs" data-testid="edit-project-audio-remove">
                    <input
                      type="checkbox"
                      checked={removeAudio}
                      onChange={(event) => setRemoveAudio(event.target.checked)}
                      data-testid="edit-project-audio-remove-checkbox"
                    />
                    Usuń audio
                  </label>
                )}
              </div>
            <div className="space-y-2" data-testid="edit-project-audio-mode-group">
              <label className="text-sm font-medium" data-testid="edit-project-audio-mode-label">
                Tryb odtwarzania dźwięku
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={audioPlaybackMode}
                onChange={(event) => setAudioPlaybackMode(event.target.value)}
                disabled={!project.audio_url && !audioFile}
                data-testid="edit-project-audio-mode-select"
              >
                <option value="once">Jednorazowo</option>
                <option value="loop">W pętli</option>
              </select>
              {!project.audio_url && !audioFile && (
                <p className="text-xs text-muted-foreground" data-testid="edit-project-audio-mode-hint">
                  Tryb dostępny po dodaniu pliku audio.
                </p>
              )}
            </div>
            <div className="space-y-2" data-testid="edit-project-ar-placement-group">
              <label className="text-sm font-medium" data-testid="edit-project-ar-placement-label">
                Typ umieszczenia AR
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={arPlacementType}
                onChange={(event) => setArPlacementType(event.target.value)}
                data-testid="edit-project-ar-placement-select"
              >
                <option value="floor">Podłoga</option>
                <option value="wall">Ściana</option>
              </select>
            </div>
            <div className="space-y-2" data-testid="edit-project-auto-start-group">
              <label className="text-sm font-medium" data-testid="edit-project-auto-start-label">
                Automatycznie uruchom AR po załadowaniu
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={autoStartAr ? "true" : "false"}
                onChange={(event) => setAutoStartAr(event.target.value === "true")}
                data-testid="edit-project-auto-start-select"
              >
                <option value="false">Nie</option>
                <option value="true">Tak</option>
              </select>
            </div>
            <div className="space-y-2" data-testid="edit-project-allow-scaling-group">
              <label className="text-sm font-medium" data-testid="edit-project-allow-scaling-label">
                Pozwól użytkownikowi skalować model
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={allowUserScaling ? "true" : "false"}
                onChange={(event) => setAllowUserScaling(event.target.value === "true")}
                data-testid="edit-project-allow-scaling-select"
              >
                <option value="true">Tak (skala zostaje)</option>
                <option value="false">Nie (podgląd, wraca do 100%)</option>
              </select>
            </div>
            <div className="space-y-2" data-testid="edit-project-animation-start-group">
              <label className="text-sm font-medium" data-testid="edit-project-animation-start-label">
                Start animacji
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={animationStartMode}
                onChange={(event) => setAnimationStartMode(event.target.value)}
                data-testid="edit-project-animation-start-select"
              >
                <option value="auto">Automatycznie</option>
                <option value="click">Po kliknięciu użytkownika</option>
              </select>
            </div>
            <div className="space-y-2" data-testid="edit-project-animation-playback-group">
              <label className="text-sm font-medium" data-testid="edit-project-animation-playback-label">
                Tryb animacji
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={animationPlaybackMode}
                onChange={(event) => setAnimationPlaybackMode(event.target.value)}
                data-testid="edit-project-animation-playback-select"
              >
                <option value="once">Raz</option>
                <option value="loop">W pętli</option>
              </select>
            </div>
            <div className="space-y-2" data-testid="edit-project-language-group">
              <label className="text-sm font-medium" data-testid="edit-project-language-label">
                Język projektu
              </label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                data-testid="edit-project-language-select"
              >
                <option value="pl">Polski</option>
                <option value="en">English</option>
              </select>
              <p className="text-xs text-muted-foreground" data-testid="edit-project-language-hint">
                Język tekstów widocznych dla użytkownika końcowego (strona projektu, AR, instrukcje).
              </p>
            </div>
            </div>
            <div
              className="rounded-lg border border-border/60 bg-muted/20 p-4"
              data-testid="edit-project-start-view-section"
            >
              <div className="space-y-1">
                <p className="text-sm font-medium" data-testid="edit-project-start-view-title">
                  Startowy widok 3D
                </p>
                <p className="text-xs text-muted-foreground" data-testid="edit-project-start-view-desc">
                  Ustaw kadr startowy (obrót + zoom) dla publicznego podglądu.
                </p>
                {initialCameraOrbit && (
                  <p className="text-xs text-muted-foreground" data-testid="edit-project-start-view-value">
                    Orbit: {initialCameraOrbit}
                  </p>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="edit-project-start-view-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowOrbitModal(true)}
                  data-testid="edit-project-start-view-edit"
                >
                  Edytuj kadr startowy
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      data-testid="edit-project-start-view-reset"
                    >
                      Przywróć domyślny widok
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="edit-project-start-view-reset-modal">
                    <AlertDialogHeader>
                      <AlertDialogTitle data-testid="edit-project-start-view-reset-title">
                        Przywrócić domyślny widok?
                      </AlertDialogTitle>
                      <AlertDialogDescription data-testid="edit-project-start-view-reset-desc">
                        Zapisany kadr zostanie usunięty. Publiczny podgląd wróci do ustawień domyślnych.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="edit-project-start-view-reset-cancel">
                        Anuluj
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleResetOrbit}
                        data-testid="edit-project-start-view-reset-confirm"
                      >
                        Przywróć
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="edit-project-actions">
              <Button type="submit" disabled={saving} data-testid="edit-project-save">
                {saving ? "Zapisywanie..." : "Zapisz zmiany"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/projects/${id}`)}
                data-testid="edit-project-cancel"
              >
                Anuluj
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      <Dialog open={showOrbitModal} onOpenChange={setShowOrbitModal}>
        <DialogContent className="max-w-3xl" data-testid="start-view-modal">
          <DialogHeader>
            <DialogTitle data-testid="start-view-modal-title">Edytuj kadr startowy</DialogTitle>
          </DialogHeader>
          <div className="h-[360px] w-full overflow-hidden rounded-lg border" data-testid="start-view-modal-viewer">
            <model-viewer
              ref={orbitViewerRef}
              src={project.glb_url}
              camera-controls
              auto-rotate
              ar
              style={{ width: "100%", height: "100%", background: "#f7f7f8" }}
              camera-orbit={initialCameraOrbit || undefined}
              data-testid="start-view-model-viewer"
            />
          </div>
          <DialogFooter className="flex flex-wrap gap-2" data-testid="start-view-modal-actions">
            <Button type="button" variant="outline" onClick={() => setShowOrbitModal(false)} data-testid="start-view-modal-cancel">
              Anuluj
            </Button>
            <Button type="button" onClick={handleSaveOrbit} data-testid="start-view-modal-save">
              Zapisz ten widok jako startowy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [statsRange, setStatsRange] = useState("week");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const publicUrl = useMemo(() => `${window.location.origin}/p/${id}`, [id]);
  const qrUrl = useMemo(() => `${window.location.origin}/p/${id}?src=qr`, [id]);
  
  // Embed code with return URL support
  // The iframe URL contains a placeholder that the client's page should replace with window.location.href
  // Or we provide a script that does it automatically
  const iframeCode = useMemo(
    () => {
      const baseUrl = PRODUCTION_URL || window.location.origin;
      // Embed code z automatycznym ustawieniem return URL przez JavaScript
      return `<!-- ARLens 3D Embed -->
<div id="arlens-${id}"></div>
<script>
(function(){
  var returnUrl = encodeURIComponent(window.location.href);
  var iframe = document.createElement('iframe');
  iframe.src = '${baseUrl}/p/${id}?embed=1&return=' + returnUrl;
  iframe.width = '100%';
  iframe.height = '600';
  iframe.style.border = '0';
  iframe.allow = 'camera; xr-spatial-tracking';
  iframe.allowFullscreen = true;
  document.getElementById('arlens-${id}').appendChild(iframe);
})();
</script>`;
    },
    [id],
  );

  useEffect(() => {
    const loadProject = async () => {
      try {
        const response = await apiClient.get(`/projects/${id}`);
        setProject(response.data);
      } catch (error) {
        toast.error("Nie udało się pobrać projektu");
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [id]);

  useEffect(() => {
    const createQr = async () => {
      try {
        const url = await QRCode.toDataURL(qrUrl, { margin: 1, width: 280 });
        setQrCode(url);
      } catch (error) {
        setQrCode("");
      }
    };
    createQr();
  }, [qrUrl]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!id) return;
      setStatsLoading(true);
      try {
        const response = await apiClient.get(`/projects/${id}/stats?range=${statsRange}`);
        setStats(response.data);
      } catch (error) {
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [id, statsRange]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(iframeCode);
    toast.success("Kod osadzenia skopiowany");
  };

  if (loading) {
    return (
      <Card data-testid="project-detail-loading">
        <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="project-detail-loading-text">
          Ładowanie projektu...
        </CardContent>
      </Card>
    );
  }

  if (!project) {
    return (
      <Card data-testid="project-detail-empty">
        <CardContent className="py-8 text-center text-sm text-muted-foreground" data-testid="project-detail-empty-text">
          Nie znaleziono projektu.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-6" data-testid="project-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-4" data-testid="project-detail-header">
        <div>
          <h1 className="text-3xl font-semibold" data-testid="project-detail-title">
            {project.name}
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="project-detail-description">
            {project.description || "Brak opisu"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="project-detail-actions">
          <Button asChild variant="outline" data-testid="project-detail-edit-link">
            <Link to={`/projects/${project.id}/edit`}>Edytuj</Link>
          </Button>
          <Button asChild variant="outline" data-testid="project-detail-preview-link">
            <a href={publicUrl} target="_blank" rel="noreferrer">
              Otwórz podgląd
            </a>
          </Button>
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]" data-testid="project-detail-grid">
        <Card data-testid="project-detail-links-card">
          <CardHeader>
            <CardTitle data-testid="project-detail-links-title">Link publiczny</CardTitle>
            <CardDescription data-testid="project-detail-links-description">
              Użyj tego URL do udostępniania podglądu 3D.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-sm" data-testid="project-detail-public-url">
              {publicUrl}
            </div>
            <div className="space-y-2" data-testid="project-detail-embed-section">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium" data-testid="project-detail-embed-label">
                  Embed code
                </p>
                <Button variant="outline" size="sm" onClick={handleCopy} data-testid="project-detail-copy-button">
                  Kopiuj
                </Button>
              </div>
              <Textarea
                value={iframeCode}
                readOnly
                rows={5}
                className="font-mono text-xs"
                data-testid="project-detail-embed-code"
              />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="project-detail-qr-card">
          <CardHeader>
            <CardTitle data-testid="project-detail-qr-title">QR Code</CardTitle>
            <CardDescription data-testid="project-detail-qr-description">
              Udostępnij podgląd na urządzenia mobilne.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {qrCode ? (
              <img
                src={qrCode}
                alt="QR"
                className="h-48 w-48 rounded-lg border border-border/60"
                data-testid="project-detail-qr-image"
              />
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="project-detail-qr-empty">
                Generowanie QR...
              </p>
            )}
            <Button
              variant="outline"
              asChild
              disabled={!qrCode}
              data-testid="project-detail-qr-download"
            >
              <a href={qrCode} download={`project-${project.id}-qr.png`}>
                Pobierz PNG
              </a>
            </Button>
          </CardContent>
        </Card>
        <Card className="lg:col-span-2" data-testid="project-detail-stats-card">
          <CardHeader>
            <CardTitle data-testid="project-detail-stats-title">Statystyki projektu</CardTitle>
            <CardDescription data-testid="project-detail-stats-description">
              Zliczane od momentu wdrożenia statystyk.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="project-detail-stats-range">
              {[
                { key: "day", label: "Dzień" },
                { key: "week", label: "Tydzień" },
                { key: "month", label: "Miesiąc" },
                { key: "all", label: "All time" },
              ].map((range) => (
                <Button
                  key={range.key}
                  variant={statsRange === range.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatsRange(range.key)}
                  data-testid={`stats-range-${range.key}`}
                >
                  {range.label}
                </Button>
              ))}
            </div>
            <div className="grid gap-4 md:grid-cols-2" data-testid="project-detail-stats-grid">
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4" data-testid="stats-qr-opens">
                <p className="text-xs text-muted-foreground">Otwarcia z QR</p>
                <p className="text-2xl font-semibold">
                  {statsLoading ? "..." : stats?.qr_opens ?? "—"}
                </p>
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4" data-testid="stats-ar-launches">
                <p className="text-xs text-muted-foreground">Uruchomienia AR</p>
                <p className="text-2xl font-semibold">
                  {statsLoading ? "..." : stats?.ar_launches ?? "—"}
                </p>
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4" data-testid="stats-ar-avg">
                <p className="text-xs text-muted-foreground">Czas w AR (średnio)</p>
                <p className="text-xl font-semibold">
                  {statsLoading
                    ? "..."
                    : stats?.ar_time_avg_seconds != null
                      ? formatDuration(stats.ar_time_avg_seconds)
                      : "Niedostępne"}
                </p>
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/30 p-4" data-testid="stats-ar-total">
                <p className="text-xs text-muted-foreground">Czas w AR (łącznie)</p>
                <p className="text-xl font-semibold">
                  {statsLoading
                    ? "..."
                    : stats?.ar_time_total_seconds != null
                      ? formatDuration(stats.ar_time_total_seconds)
                      : "Niedostępne"}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="stats-ar-note">
              Czas w AR szacowany na podstawie powrotu do strony.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const PublicViewerPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [arSupported, setArSupported] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [lastError, setLastError] = useState("");
  const [deviceInfo, setDeviceInfo] = useState({
    isIOS: false,
    isAndroid: false,
    userAgent: "",
  });
  const [showArModal, setShowArModal] = useState(false);
  const [arModalType, setArModalType] = useState(null);
  const [arLaunchRequested, setArLaunchRequested] = useState(false);
  const [arAttemptStartedAt, setArAttemptStartedAt] = useState(null);
  const [arSessionLikelyOpened, setArSessionLikelyOpened] = useState(false);
  const [arFallbackHandled, setArFallbackHandled] = useState(false);
  const [arReturnedFromSession, setArReturnedFromSession] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const [hasRenderedModel, setHasRenderedModel] = useState(false);
  const [hasAnimations, setHasAnimations] = useState(false);
  const [animationPlaying, setAnimationPlaying] = useState(false);
  const [loadingHint, setLoadingHint] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [hasSeenProgress, setHasSeenProgress] = useState(false);
  const [browserInfo, setBrowserInfo] = useState({
    name: "",
    isChrome: false,
    isSamsung: false,
    isInApp: false,
    isMessenger: false,
  });
  const [sceneViewerUrl, setSceneViewerUrl] = useState("");
  const [publicProjectUrl, setPublicProjectUrl] = useState("");
  const [showDesktopQrModal, setShowDesktopQrModal] = useState(false);
  const [desktopQrCode, setDesktopQrCode] = useState("");
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [showWebXR, setShowWebXR] = useState(false);
  const [webXRSupported, setWebXRSupported] = useState(false);
  
  // Return URL from embed (passed via URL param)
  const embedReturnUrl = searchParams.get("return");
  
  // Debug mode
  const debugMode = searchParams.get("debug") === "1";
  
  const autoStartAttemptedRef = useRef(false);
  const pageViewLoggedRef = useRef(false);
  const arAnalyticsRef = useRef({ startedAt: null, likelyOpened: false, logged: false });
  const audioRef = useRef(null);
  const viewerInstanceRef = useRef(null);
  const iosQuickLookRef = useRef(null);
  const iosArActiveRef = useRef(false); // iOS audio session active (audio started)
  const iosQuickLookLaunchedRef = useRef(false); // Quick Look actually launched
  const wasHiddenRef = useRef(false); // page went background after AR launch
  const arTimeoutRef = useRef(null);
  const audioStartTimeoutRef = useRef(null);
  const viewerWarningShownRef = useRef(false);
  const progressTimeoutRef = useRef(null);
  const loadingHintTimeoutRef = useRef(null);
  const arStateRef = useRef({
    launchRequested: false,
    sessionLikelyOpened: false,
    fallbackHandled: false,
    attemptStartedAt: null,
  });

  useEffect(() => {
    setIsClient(true);
    // Detect if running inside an iframe (embed mode)
    try {
      setIsEmbedded(window.self !== window.top);
    } catch (e) {
      // Cross-origin iframe access throws error - means we're embedded
      setIsEmbedded(true);
    }
    if (typeof navigator !== "undefined") {
      const ua = navigator.userAgent || "";
      const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
      const isAndroid = /Android/.test(ua);
      const isSamsung = /SamsungBrowser/i.test(ua);
      const isMessenger = /Messenger|FB_IAB|FBAN\/Messenger/i.test(ua);
      const isInApp = /FBAN|FBAV|Instagram|Line|Twitter|Snapchat|LinkedInApp|wv|; wv/i.test(ua) || isMessenger;
      const isChrome = /Chrome/i.test(ua) && !/Edg|OPR|SamsungBrowser|UCBrowser/i.test(ua) && !isInApp;
      let name = "Inne";
      if (isInApp) {
        name = isMessenger ? "Messenger" : "In-app";
      } else if (isSamsung) {
        name = "Samsung Internet";
      } else if (isChrome) {
        name = "Chrome";
      }
      setDeviceInfo({ isIOS, isAndroid, userAgent: ua });
      setBrowserInfo({ name, isChrome, isSamsung, isInApp, isMessenger });
      
      // Check WebXR support (only on Android Chrome, not in webviews)
      if (WEBXR_ENABLED && isAndroid && isChrome && !isInApp && navigator.xr) {
        navigator.xr.isSessionSupported("immersive-ar").then((supported) => {
          setWebXRSupported(supported);
          console.info("[WebXR] Support check:", { supported, isAndroid, isChrome });
        }).catch(() => {
          setWebXRSupported(false);
        });
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setPublicProjectUrl(window.location.origin + window.location.pathname);
  }, []);

  const qrPublicUrl = useMemo(() => {
    if (!publicProjectUrl) return "";
    return `${publicProjectUrl}?src=qr`;
  }, [publicProjectUrl]);

  useEffect(() => {
    const loadQr = async () => {
      if (!qrPublicUrl) return;
      try {
        const url = await QRCode.toDataURL(qrPublicUrl, { margin: 1, width: 320 });
        setDesktopQrCode(url);
      } catch (error) {
        setDesktopQrCode("");
      }
    };
    loadQr();
  }, [qrPublicUrl]);

  useEffect(() => {
    const loadProject = async () => {
      try {
        const response = await apiClient.get(`/public/projects/${id}`);
        console.info("[PROJECT_LOAD]", {
          isEmbedded: (() => { try { return window.self !== window.top; } catch(e) { return true; } })(),
          projectId: id,
          glbUrl: response.data?.glb_url,
          backendUrl: BACKEND_URL
        });
        setProject(response.data);
      } catch (error) {
        console.error("[PROJECT_LOAD_ERROR]", { isEmbedded: (() => { try { return window.self !== window.top; } catch(e) { return true; } })(), error: error.message });
        setProject(null);
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [id]);

  const logEvent = (eventType, extra = {}) => {
    if (!id) return;
    try {
      const payload = {
        event_type: eventType,
        device_type: deviceInfo.isIOS || deviceInfo.isAndroid ? "mobile" : "desktop",
        browser_family: browserInfo.name || "Inne",
        ...extra,
      };
      const body = JSON.stringify(payload);
      const endpoint = `${API}/public/projects/${id}/events`;
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(endpoint, blob);
      } else {
        fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        });
      }
    } catch (error) {
      console.warn("Event log failed", error);
    }
  };

  useEffect(() => {
    if (!project || !isClient || pageViewLoggedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const src = params.get("src");
    logEvent("page_view", { source: src || "direct" });
    if (src === "qr") {
      logEvent("qr_open", { source: "qr" });
    }
    pageViewLoggedRef.current = true;
  }, [project, isClient]);

  useEffect(() => {
    setIsLoaded(false);
    setLoadError("");
    setLastError("");
    setAudioStarted(false);
    setHasRenderedModel(false);
    setHasAnimations(false);
    setAnimationPlaying(false);
    setLoadingHint(false);
    setLoadingProgress(0);
    setHasSeenProgress(false);
    viewerWarningShownRef.current = false;
    autoStartAttemptedRef.current = false;
    if (audioStartTimeoutRef.current) {
      clearTimeout(audioStartTimeoutRef.current);
      audioStartTimeoutRef.current = null;
    }
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
      progressTimeoutRef.current = null;
    }
    if (loadingHintTimeoutRef.current) {
      clearTimeout(loadingHintTimeoutRef.current);
      loadingHintTimeoutRef.current = null;
    }
  }, [project?.id]);

  useEffect(() => {
    if (audioRef.current && project?.audio_url) {
      audioRef.current.loop = project.audio_playback_mode === "loop";
    }
  }, [project?.audio_playback_mode, project?.audio_url]);

  // Ref to track if model was ever successfully rendered - persists across visibility changes
  const modelEverRenderedRef = useRef(false);

  useEffect(() => {
    if (hasRenderedModel) {
      modelEverRenderedRef.current = true;
    }
  }, [hasRenderedModel]);

  useEffect(() => {
    // Skip timeout setup if model was ever rendered successfully
    if (modelEverRenderedRef.current) return;
    if (!project || !project.glb_url || !isClient || isLoaded || loadError) return;
    if (hasRenderedModel) return;
    if (!loadingHintTimeoutRef.current) {
      loadingHintTimeoutRef.current = setTimeout(() => {
        setLoadingHint(true);
      }, 8000);
    }
    if (!progressTimeoutRef.current) {
      progressTimeoutRef.current = setTimeout(() => {
        // Double-check: never show error if model was ever rendered
        if (modelEverRenderedRef.current) return;
        if (!isLoaded && !hasRenderedModel && !document.hidden) {
          setLoadError(t.modelLoadError);
          setLastError("timeout");
        }
      }, 45000);
    }
    return () => {};
  }, [project, isClient, isLoaded, loadError, hasRenderedModel]);

  useEffect(() => {
    const logState = (eventType) => {
      console.info("[MODEL_TAB_STATE]", eventType, {
        hidden: document.hidden,
        hasRenderedModel,
        modelEverRendered: modelEverRenderedRef.current,
        isLoaded,
        loadError,
      });
    };

    const handleVisibility = () => {
      logState("visibilitychange");
      if (document.hidden) {
        // Clear timeouts when page goes to background
        if (progressTimeoutRef.current) {
          clearTimeout(progressTimeoutRef.current);
          progressTimeoutRef.current = null;
        }
        if (loadingHintTimeoutRef.current) {
          clearTimeout(loadingHintTimeoutRef.current);
          loadingHintTimeoutRef.current = null;
        }
      } else {
        // Page returned to foreground
        console.info("[MODEL_TAB_STATE] returned to foreground, modelEverRendered:", modelEverRenderedRef.current);
      }
    };

    const handleFocus = () => logState("focus");
    const handleBlur = () => logState("blur");
    const handlePageShow = () => logState("pageshow");

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [hasRenderedModel, isLoaded, loadError]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const debugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("debug") === "1";
  }, []);

  useEffect(() => {
    arStateRef.current = {
      launchRequested: arLaunchRequested,
      sessionLikelyOpened: arSessionLikelyOpened,
      fallbackHandled: arFallbackHandled,
      attemptStartedAt: arAttemptStartedAt,
    };
  }, [arLaunchRequested, arSessionLikelyOpened, arFallbackHandled, arAttemptStartedAt]);

  useEffect(() => {
    return () => {
      if (arTimeoutRef.current) {
        clearTimeout(arTimeoutRef.current);
      }
      if (audioStartTimeoutRef.current) {
        clearTimeout(audioStartTimeoutRef.current);
      }
      if (progressTimeoutRef.current) {
        clearTimeout(progressTimeoutRef.current);
      }
      if (loadingHintTimeoutRef.current) {
        clearTimeout(loadingHintTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!deviceInfo.isAndroid) return;
    const referrer = document.referrer || "";
    const pending = sessionStorage.getItem("ar_launch_requested");
    if (pending && referrer.includes("arvr.google.com")) {
      setShowArModal(true);
      setArModalType("fail");
      sessionStorage.removeItem("ar_launch_requested");
    }
  }, [deviceInfo.isAndroid]);

  // iOS: stop + reset audio ONLY on a real return from Quick Look to the 3D
  // preview. Launching Quick Look makes Safari go hidden/blur - that is NOT a
  // return and must not stop audio. We use explicit session state:
  //  - iosArActiveRef: audio session started (first CTA)
  //  - iosQuickLookLaunchedRef: Quick Look actually launched ("View in AR")
  //  - wasHiddenRef: page went to background after launch
  // A return = visible AND wasHidden AND quickLookLaunched. Only visibilitychange
  // + pagehide/pageshow are used (blur/focus are too noisy on open).
  useEffect(() => {
    if (!deviceInfo.isIOS) return;

    const stopPageAudio = () => {
      const el = audioRef.current;
      if (el) {
        try {
          el.pause();
          el.currentTime = 0;
        } catch (e) {
          /* ignore */
        }
      }
      if (audioStartTimeoutRef.current) {
        clearTimeout(audioStartTimeoutRef.current);
        audioStartTimeoutRef.current = null;
      }
      setIsPlaying(false);
      console.info("[AUDIO iOS] audio stopped and reset");
    };

    const markHidden = () => {
      if (iosArActiveRef.current && iosQuickLookLaunchedRef.current) {
        wasHiddenRef.current = true;
        console.info("[AUDIO iOS] page hidden (Quick Look active) - audio untouched");
      }
    };
    const markReturn = () => {
      // Only a genuine return: audio session active, Quick Look was launched,
      // and the page had actually gone to background.
      if (!iosArActiveRef.current || !iosQuickLookLaunchedRef.current || !wasHiddenRef.current) return;
      console.info("[AUDIO iOS] detected return from Quick Look");
      stopPageAudio();
      iosArActiveRef.current = false;
      iosQuickLookLaunchedRef.current = false;
      wasHiddenRef.current = false;
      setArReturnedFromSession(true);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") markHidden();
      else markReturn();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", markHidden);
    window.addEventListener("pageshow", markReturn);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", markHidden);
      window.removeEventListener("pageshow", markReturn);
    };
  }, [deviceInfo.isIOS]);

  useEffect(() => {
    const handleVisibility = () => {
      const analytics = arAnalyticsRef.current;
      if (analytics?.startedAt) {
        if (document.visibilityState === "hidden") {
          analytics.likelyOpened = true;
        } else if (document.visibilityState === "visible") {
          if (analytics.likelyOpened && !analytics.logged) {
            const durationSeconds = Math.round((Date.now() - analytics.startedAt) / 1000);
            if (durationSeconds >= 2 && durationSeconds <= 600) {
              logEvent("ar_session_estimated", { duration_seconds: durationSeconds });
            }
            analytics.logged = true;
          }
          if (analytics.logged) {
            analytics.startedAt = null;
            analytics.likelyOpened = false;
          }
        }
      }
      if (!arLaunchRequested || !deviceInfo.isAndroid) return;

      if (document.visibilityState === "hidden") {
        setArSessionLikelyOpened(true);
        if (arTimeoutRef.current) {
          clearTimeout(arTimeoutRef.current);
          arTimeoutRef.current = null;
        }
        return;
      }

      if (document.visibilityState === "visible") {
        if (arSessionLikelyOpened) {
          // Returned from Scene Viewer - stop + reset page audio
          if (audioStartTimeoutRef.current) {
            clearTimeout(audioStartTimeoutRef.current);
            audioStartTimeoutRef.current = null;
          }
          if (audioRef.current) {
            try {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
            } catch (e) {
              /* ignore */
            }
          }
          setIsPlaying(false);
          setArLaunchRequested(false);
          setArSessionLikelyOpened(false);
          setArAttemptStartedAt(null);
          setArFallbackHandled(false);
          setShowArModal(false);
          setArModalType(null);
          setArReturnedFromSession(true);
          sessionStorage.removeItem("ar_launch_requested");
          return;
        }

        const startedAt = arAttemptStartedAt || Date.now();
        const elapsed = Date.now() - startedAt;

        if (elapsed >= 2500 && !arFallbackHandled) {
          clearAudioStartTimeout();
          setShowArModal(true);
          setArModalType("fail");
          setArFallbackHandled(true);
          return;
        }

        if (!arFallbackHandled) {
          const delay = Math.max(0, 2500 - elapsed);
          if (arTimeoutRef.current) {
            clearTimeout(arTimeoutRef.current);
          }
          arTimeoutRef.current = setTimeout(() => {
            clearAudioStartTimeout();
            setShowArModal(true);
            setArModalType("fail");
            setArFallbackHandled(true);
          }, delay);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (arTimeoutRef.current) {
        clearTimeout(arTimeoutRef.current);
        arTimeoutRef.current = null;
      }
    };
  }, [
    arLaunchRequested,
    arAttemptStartedAt,
    arSessionLikelyOpened,
    arFallbackHandled,
    deviceInfo.isAndroid,
  ]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowDesktopQrModal(false);
      }
    };
    if (showDesktopQrModal) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => window.removeEventListener("keydown", handleEsc);
  }, [showDesktopQrModal]);

  // Auto-start AR from project settings
  useEffect(() => {
    if (!project?.auto_start_ar || !isLoaded || autoStartAttemptedRef.current) return;
    autoStartAttemptedRef.current = true;
    handleAutoStartAr();
  }, [project?.auto_start_ar, isLoaded]);

  const t = getT(project?.language || "pl");

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="public-loading">
        <div className="space-y-2 text-center">
          <p className="text-sm text-muted-foreground" data-testid="public-loading-text">
            {t.loadingPreview}
          </p>
          {loadingHint && (
            <p className="text-xs text-muted-foreground" data-testid="public-loading-hint">
              {t.loadingHint}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="public-not-found">
        <p className="text-sm text-muted-foreground" data-testid="public-not-found-text">
          {t.projectNotFound}
        </p>
      </div>
    );
  }

  if (!project.glb_url) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="public-missing-glb">
        <div className="space-y-2 text-center">
          <p className="text-sm text-muted-foreground" data-testid="public-missing-glb-text">
            {t.missingGlb}
          </p>
        </div>
      </div>
    );
  }

  const showIosMissingUsdz = deviceInfo.isIOS && !project.usdz_url;
  const showArButton = !showIosMissingUsdz && !loadError;

  const handleModelError = (message) => {
    setLastError(message || "error");
    // Use ref to check if model was EVER rendered successfully (persists across tab switches)
    if (modelEverRenderedRef.current || hasRenderedModel) {
      if (!viewerWarningShownRef.current) {
        console.info("[MODEL_TAB_STATE] handleModelError: model was already rendered, ignoring error");
        viewerWarningShownRef.current = true;
      }
      return;
    }
    setLoadError(t.modelLoadError);
  };

  const handleModelLoaded = () => {
    console.info("[MODEL_TAB_STATE] handleModelLoaded: model rendered successfully");
    modelEverRenderedRef.current = true;
    setIsLoaded(true);
    setLoadError("");
    setHasRenderedModel(true);
    setLoadingProgress(100);
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
      progressTimeoutRef.current = null;
    }
    if (loadingHintTimeoutRef.current) {
      clearTimeout(loadingHintTimeoutRef.current);
      loadingHintTimeoutRef.current = null;
    }
  };

  const handleModelProgress = (event) => {
    // Skip if model was ever rendered successfully
    if (modelEverRenderedRef.current) return;
    const progressValue = typeof event?.detail?.totalProgress === "number"
      ? Math.round(event.detail.totalProgress * 100)
      : null;
    if (progressValue != null) {
      setLoadingProgress(progressValue);
      if (progressValue > 0) {
        setHasSeenProgress(true);
      }
    }
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
    }
    progressTimeoutRef.current = setTimeout(() => {
      // Double-check: never show error if model was ever rendered
      if (modelEverRenderedRef.current) return;
      if (!isLoaded && !hasRenderedModel && !document.hidden) {
        setLoadError(t.modelLoadError);
        setLastError("stalled");
      }
    }, 30000);
  };

  const handleAnimationToggle = () => {
    const viewer = viewerInstanceRef.current;
    if (!viewer || !hasAnimations) return;
    if (animationPlaying) {
      viewer.pause();
      setAnimationPlaying(false);
    } else {
      if (project?.animation_playback_mode === "once") {
        viewer.play({ repetitions: 1 });
      } else {
        viewer.play();
      }
      setAnimationPlaying(true);
    }
  };

  const progressValue = Math.min(100, Math.max(0, loadingProgress));
  const progressRadius = 46;
  const progressCircumference = 2 * Math.PI * progressRadius;
  const progressOffset = progressCircumference - (progressValue / 100) * progressCircumference;

  const handleArClick = async () => {
    setArReturnedFromSession(false);
    
    // For Android, check WebXR support synchronously at click time
    let canUseWebXR = webXRSupported;
    if (deviceInfo.isAndroid && browserInfo.isChrome && !browserInfo.isInApp && WEBXR_ENABLED && navigator.xr) {
      try {
        canUseWebXR = await navigator.xr.isSessionSupported("immersive-ar");
        console.info("[AR_DECISION] WebXR isSessionSupported:", canUseWebXR);
      } catch (e) {
        canUseWebXR = false;
        console.info("[AR_DECISION] WebXR check failed:", e.message);
      }
    } else {
      console.info("[AR_DECISION] Skipping WebXR check", { 
        isAndroid: deviceInfo.isAndroid, 
        isChrome: browserInfo.isChrome,
        isInApp: browserInfo.isInApp,
        WEBXR_ENABLED,
        hasXR: !!navigator.xr
      });
    }
    
    // Diagnostic log for embed AR decision
    const arAction = (() => {
      if (deviceInfo.isIOS) return "OPEN_QUICKLOOK";
      if (!browserInfo.isChrome) return "SHOW_OPEN_IN_CHROME";
      if (canUseWebXR && project?.ar_placement_type !== "wall") {
        return isEmbedded ? "OPEN_WEBXR_TOPLEVEL" : "OPEN_WEBXR_INLINE";
      }
      return "OPEN_SCENEVIEWER";
    })();
    
    console.info("[EMBED_AR_CLICK]", {
      isEmbedded,
      isAndroid: deviceInfo.isAndroid,
      isChrome: browserInfo.isChrome,
      isInApp: browserInfo.isInApp,
      webxrSupported: canUseWebXR,
      action: arAction
    });
    
    // EMBED: Handle differently per platform
    if (isEmbedded && project?.id) {
      // iOS embed - show modal with Quick Look button (don't navigate)
      if (deviceInfo.isIOS) {
        console.info("[EMBED_AR] iOS - showing Quick Look modal");
        setShowArModal(true);
        setArModalType("ios-quicklook");
        return;
      }
      
      // Android/other embed - simple redirect to launch-ar (all logic happens there)
      // Pass return URL so user can return to client's page after AR
      let launchArUrl = `${PRODUCTION_URL}/launch-ar/${project.id}`;
      
      // Use return URL from embed param (passed via iframe URL) or fall back to referrer
      const returnUrl = embedReturnUrl || document.referrer;
      
      if (returnUrl && returnUrl.startsWith("http")) {
        launchArUrl += `?return=${encodeURIComponent(returnUrl)}`;
        console.info("[EMBED_AR] Return URL:", returnUrl, embedReturnUrl ? "(from embed param)" : "(from referrer)");
      } else {
        console.info("[EMBED_AR] No valid return URL");
      }
      
      console.info("[EMBED_AR] Android - redirecting to launch-ar", { launchArUrl, returnUrl });
      // Use top-level navigation to escape iframe
      if (window.top !== window.self) {
        window.top.location.href = launchArUrl;
      } else {
        window.location.href = launchArUrl;
      }
      return;
    }
    
    if (deviceInfo.isAndroid && project?.glb_url) {
      // WebXR path: Android + Chrome + WebXR supported + floor placement
      const isFloorPlacement = project?.ar_placement_type !== "wall";
      
      if (WEBXR_ENABLED && canUseWebXR && isFloorPlacement && browserInfo.isChrome) {
        console.info("[WebXR] OPEN_WEBXR_INLINE");
        // Android WebXR: page audio must stay silent until model placement
        // (handled inside WebXRViewer). Cancel any pending page-audio timeout.
        clearAudioStartTimeout();
        stopAudio();
        setShowWebXR(true);
        return;
      }
      
      // Non-Chrome browser - show modal
      if (!browserInfo.isChrome) {
        setShowArModal(true);
        setArModalType("browser");
        return;
      }
      
      // Scene Viewer fallback (WebXR not supported or wall placement)
      const fileUrl = encodeURIComponent(project.glb_url);
      const title = encodeURIComponent(project.name || "Model 3D");
      const verticalPlacement = project?.ar_placement_type === "wall" ? "&enable_vertical_placement=true" : "";
      const sceneViewerUrl = `https://arvr.google.com/scene-viewer/1.0?file=${fileUrl}&mode=ar_only&title=${title}${verticalPlacement}`;
      
      console.info("[AR] OPEN_SCENEVIEWER", { sceneViewerUrl, isEmbedded });
      setSceneViewerUrl(sceneViewerUrl);
      
      // Scene Viewer launch with timeout fallback
      setArLaunchRequested(true);
      setArAttemptStartedAt(Date.now());
      setArSessionLikelyOpened(false);
      setArFallbackHandled(false);
      setShowArModal(false);
      setArModalType(null);
      if (arTimeoutRef.current) {
        clearTimeout(arTimeoutRef.current);
      }
      sessionStorage.setItem("ar_launch_requested", String(Date.now()));
      arTimeoutRef.current = setTimeout(() => {
        const current = arStateRef.current;
        if (
          current.launchRequested &&
          !current.sessionLikelyOpened &&
          !current.fallbackHandled &&
          document.visibilityState === "visible"
        ) {
          setShowArModal(true);
          setArModalType("fail");
          setArFallbackHandled(true);
        }
      }, 2500);
      
      window.location.href = sceneViewerUrl;
      return;
    }
    // iOS Quick Look (non-embedded): audio already started by the CTA. Only
    // mark Quick Look as launched and activate - do NOT touch audio.
    if (deviceInfo.isIOS) {
      iosQuickLookLaunchedRef.current = true;
    }
    viewerInstanceRef.current?.activateAR?.();
  };

  // Fallback from WebXR to Scene Viewer
  const handleWebXRFallback = () => {
    clearAudioStartTimeout();
    stopAudio();
    setShowWebXR(false);
    if (project?.glb_url) {
      console.info("[WebXR] Falling back to Scene Viewer", { glbUrl: project.glb_url, projectId: id });
      openSceneViewer(project.glb_url, project.name, project.ar_placement_type, id);
    }
  };

  const handleWebXRClose = () => {
    clearAudioStartTimeout();
    stopAudio();
    setShowWebXR(false);
  };

  const playAudio = async ({ silent = false } = {}) => {
    if (!audioRef.current) return false;
    try {
      await audioRef.current.play();
      setIsPlaying(true);
      setAudioStarted(true);
      return true;
    } catch (error) {
      setIsPlaying(false);
      if (silent) {
        console.warn("Audio play failed", error);
      } else {
        toast.error(t.soundFailed);
      }
      return false;
    }
  };

  const prewarmAudio = async () => {
    if (!audioRef.current) return;
    try {
      // Unlock audio silently within the user gesture (no audible blip).
      audioRef.current.muted = true;
      await audioRef.current.play();
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } catch (error) {
      console.warn("Audio prewarm failed", error);
    } finally {
      if (audioRef.current) audioRef.current.muted = false;
    }
  };

  const clearAudioStartTimeout = () => {
    if (audioStartTimeoutRef.current) {
      clearTimeout(audioStartTimeoutRef.current);
      audioStartTimeoutRef.current = null;
    }
  };

  // iOS audio session. Called from the FIRST CTA ("View in AR with sound")
  // user gesture. Plays audio_ios_url immediately; its first 5s are silent
  // (baked into the file), so no setTimeout/delayed play is needed. Launching
  // Quick Look afterwards must NOT touch this audio.
  const IOS_SILENCE_SEC = 5;
  const startIosArAudioSession = () => {
    const el = audioRef.current;
    if (!el) return; // project has no audio - nothing to do
    clearAudioStartTimeout();
    iosArActiveRef.current = true;
    iosQuickLookLaunchedRef.current = false;
    wasHiddenRef.current = false;
    console.info("[AUDIO iOS] CTA 'with sound' - start audio_ios_url (5s silence prefix) in gesture");
    try {
      el.currentTime = 0;
      el.muted = false;
      const p = el.play();
      if (p && p.then) {
        p.then(() => {
          setIsPlaying(true);
          setAudioStarted(true);
          console.info("[AUDIO iOS] playback started");
        }).catch((err) => {
          setIsPlaying(false);
          console.warn(`[AUDIO iOS] playback rejected: ${err.name} / ${err.message}`);
        });
      } else {
        setIsPlaying(true);
        setAudioStarted(true);
      }
    } catch (err) {
      console.warn(`[AUDIO iOS] playback rejected: ${err.name} / ${err.message}`);
    }
  };

  // Stop + reset the iOS audio session (real return from Quick Look, or modal
  // closed without launching AR).
  const endIosArAudioSession = () => {
    const el = audioRef.current;
    if (el) {
      try {
        el.pause();
        el.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
    }
    clearAudioStartTimeout();
    iosArActiveRef.current = false;
    iosQuickLookLaunchedRef.current = false;
    wasHiddenRef.current = false;
    setIsPlaying(false);
    console.info("[AUDIO iOS] audio session ended - stopped and reset");
  };

  // Loop handling for the iOS silence-prefixed asset: on each end, restart from
  // the end of the silence (skip the 5s silence on subsequent loops).
  const handleAudioEnded = () => {
    const el = audioRef.current;
    const iosLoop =
      deviceInfo.isIOS &&
      project?.audio_ios_url &&
      project?.audio_playback_mode === "loop";
    if (iosLoop && el) {
      try {
        el.currentTime = IOS_SILENCE_SEC;
        el.play().catch(() => {});
      } catch (e) {
        /* ignore */
      }
      return;
    }
    setIsPlaying(false);
  };

  const stopAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  const toggleAudioPlayback = async () => {
    if (!audioRef.current || !audioStarted) {
      toast.message(t.soundToast);
      return;
    }
    if (isPlaying) {
      stopAudio();
    } else {
      await playAudio();
    }
  };

  const handlePrimaryCtaClick = async () => {
    logEvent("ar_click", { source: "cta" });
    const isDesktop = !deviceInfo.isIOS && !deviceInfo.isAndroid;
    if (isDesktop) {
      clearAudioStartTimeout();
      setShowDesktopQrModal(true);
      return;
    }
    arAnalyticsRef.current = { startedAt: Date.now(), likelyOpened: false, logged: false };
    if (project?.audio_url) {
      if (deviceInfo.isAndroid && !browserInfo.isChrome) {
        clearAudioStartTimeout();
        stopAudio();
        setAudioStarted(false);
        handleArClick();
        return;
      }
      // iOS: FIRST CTA starts the audio session now (audio_ios_url, first 5s
      // silent), then opens the modal (embed) or activates (non-embed).
      if (deviceInfo.isIOS) {
        startIosArAudioSession();
        handleArClick();
        return;
      }
      // Android Chrome: WebXR handles audio on placement; Scene Viewer fallback
      // uses the page audio via the timeout below.
      clearAudioStartTimeout();
      await prewarmAudio();
      setAudioStarted(true);
      handleArClick();
      audioStartTimeoutRef.current = setTimeout(async () => {
        const isDesktopNow = !deviceInfo.isIOS && !deviceInfo.isAndroid;
        if (isDesktopNow) return;
        if (deviceInfo.isAndroid && !browserInfo.isChrome) return;
        await playAudio({ silent: true });
      }, 5000);
      return;
    }
    handleArClick();
  };

  const handleAutoStartAr = async () => {
    const isDesktop = !deviceInfo.isIOS && !deviceInfo.isAndroid;
    if (isDesktop) {
      setShowDesktopQrModal(true);
      return;
    }
    if (project?.audio_url) {
      if (deviceInfo.isAndroid && !browserInfo.isChrome) {
        handleArClick();
        return;
      }
      clearAudioStartTimeout();
      handleArClick();
      audioStartTimeoutRef.current = setTimeout(async () => {
        const isDesktopNow = !deviceInfo.isIOS && !deviceInfo.isAndroid;
        if (isDesktopNow) return;
        if (deviceInfo.isAndroid && !browserInfo.isChrome) return;
        await playAudio({ silent: true });
      }, 5000);
      return;
    }
    handleArClick();
  };

  const handleCopyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicProjectUrl);
      toast.success(t.linkCopied);
    } catch (error) {
      toast.error(t.copyFailed);
    }
  };

  const handleOpenInChrome = () => {
    if (!publicProjectUrl) return;
    try {
      const url = new URL(publicProjectUrl);
      const scheme = url.protocol.replace(":", "");
      const intentUrl = `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=${scheme};package=com.android.chrome;end;`;
      window.location.href = intentUrl;
    } catch (error) {
      toast.error(t.openInChromeFailed);
    }
  };

  // Render WebXR viewer if active
  if (showWebXR && project?.glb_url) {
    return (
      <WebXRViewer
        glbUrl={project.glb_url}
        projectName={project.name}
        onClose={handleWebXRClose}
        onFallbackToSceneViewer={handleWebXRFallback}
        allowUserScaling={project.allow_user_scaling !== false}
        debugMode={debugMode}
        language={project.language || "pl"}
        audioUrl={project.audio_url}
        audioPlaybackMode={project.audio_playback_mode}
      />
    );
  }

  return (
    <div className="relative min-h-screen bg-white" data-testid="public-viewer-page">
      <div className="public-viewer-wrapper" data-testid="public-viewer-wrapper">
        {loadError ? (
          <div className="public-fallback" data-testid="public-fallback">
            <p className="public-fallback-text" data-testid="public-viewer-fallback-text">
              {loadError}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="public-fallback-link"
              data-testid="public-viewer-refresh-button"
            >
              {t.refreshPage}
            </button>
          </div>
        ) : (
          isClient && (
            <ModelViewerClient
              glbUrl={project.glb_url}
              usdzUrl={project.usdz_url}
              arPlacement={project.ar_placement_type}
              initialCameraOrbit={project.initial_camera_orbit}
              animationStartMode={project.animation_start_mode}
              animationPlaybackMode={project.animation_playback_mode}
              onAnimationAvailable={setHasAnimations}
              onAnimationPlayState={setAnimationPlaying}
              onProgress={handleModelProgress}
              onError={handleModelError}
              onLoaded={handleModelLoaded}
              onArSupportChange={setArSupported}
              onViewerReady={(viewer) => {
                viewerInstanceRef.current = viewer;
              }}
            />
          )
        )}
        {!loadError && !isLoaded && (
          <div className="public-loading-overlay" data-testid="public-loading-overlay">
            <div className="public-loading-indicator" data-testid="public-loading-indicator">
              <svg className="public-loading-ring" viewBox="0 0 120 120" data-testid="public-loading-ring">
                <circle cx="60" cy="60" r={progressRadius} className="public-loading-track" />
                <circle
                  cx="60"
                  cy="60"
                  r={progressRadius}
                  className="public-loading-progress"
                  strokeDasharray={progressCircumference}
                  strokeDashoffset={progressOffset}
                />
              </svg>
              <div className="public-loading-percent" data-testid="public-loading-percent">
                {progressValue}%
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="public-overlay" data-testid="public-overlay">
        <div className="public-title" data-testid="public-title-block">
          <h1 className="public-title-text" data-testid="public-title">
            {project.name}
          </h1>
          <p className="public-description" data-testid="public-description">
            {project.description || t.defaultDescription}
          </p>
        </div>

        {showArButton && (
          <div className="public-ar-actions" data-testid="public-ar-actions">
            {hasAnimations && (
              <button
                className="public-icon-button public-animation-button"
                onClick={handleAnimationToggle}
                data-testid="public-animation-toggle"
                aria-label="Animacja"
              >
                {animationPlaying ? <Pause size={18} /> : <Play size={18} />}
              </button>
            )}
            {project.audio_url && (
              <button
                className={`public-icon-button ${audioStarted ? "" : "public-icon-button--disabled"}`}
                onClick={toggleAudioPlayback}
                data-testid="public-audio-mute"
                aria-label="Audio"
              >
                {isPlaying ? <Volume2 size={18} /> : <VolumeX size={18} />}
              </button>
            )}
            <button
              className={`public-ar-button ${project.audio_url ? "public-ar-button--compact" : ""}`}
              onClick={handlePrimaryCtaClick}
              data-testid="public-ar-button"
            >
              {project.audio_url ? t.viewInArWithSound : t.viewInAr}
            </button>
          </div>
        )}

        <div className="public-footer" data-testid="public-footer">
          <span className="public-footer-text" data-testid="public-footer-text">
            {showIosMissingUsdz
              ? t.iosNeedsUsdz
              : t.pressViewInAr}
          </span>
        </div>

        {project.audio_url && arReturnedFromSession && !isPlaying && (
          <button
            className="public-audio-resume"
            onClick={playAudio}
            data-testid="public-audio-resume"
          >
            {t.resumeSound}
          </button>
        )}
      </div>

      {project.audio_url && (
        <audio
          ref={audioRef}
          src={deviceInfo.isIOS && project.audio_ios_url ? project.audio_ios_url : project.audio_url}
          loop={project.audio_playback_mode === "loop" && !(deviceInfo.isIOS && project.audio_ios_url)}
          onEnded={handleAudioEnded}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onError={() => setIsPlaying(false)}
          data-testid="audio-element"
        />
      )}

      {showDesktopQrModal && (
        <div
          className={`public-qr-modal ${isEmbedded ? 'public-qr-modal--embed' : ''}`}
          onClick={() => setShowDesktopQrModal(false)}
          data-testid="public-qr-modal"
        >
          <div
            className={`public-qr-card ${isEmbedded ? 'public-qr-card--embed' : ''}`}
            onClick={(event) => event.stopPropagation()}
            data-testid="public-qr-card"
          >
            <button
              className="public-qr-close"
              onClick={() => setShowDesktopQrModal(false)}
              data-testid="public-qr-close"
              aria-label="Zamknij"
            >
              ×
            </button>
            <h3 className={`public-qr-title ${isEmbedded ? 'public-qr-title--embed' : ''}`} data-testid="public-qr-title">
              {isEmbedded ? t.scanQrTitle : t.seeModelInSpace}
            </h3>
            {desktopQrCode ? (
              <img
                src={desktopQrCode}
                alt="QR"
                className={`public-qr-image ${isEmbedded ? 'public-qr-image--embed' : ''}`}
                data-testid="public-qr-image"
              />
            ) : (
              <div className={`public-qr-image public-qr-placeholder ${isEmbedded ? 'public-qr-image--embed' : ''}`} data-testid="public-qr-placeholder">
                {t.generatingQr}
              </div>
            )}
            <ol className={`public-qr-steps ${isEmbedded ? 'public-qr-steps--embed' : ''}`} data-testid="public-qr-steps">
              <li>{t.qrStep1}</li>
              <li>{t.qrStep2}</li>
              <li>{t.qrStep3}</li>
              <li>{t.qrStep4}</li>
            </ol>
            {!isEmbedded && (
              <p className="public-qr-footnote" data-testid="public-qr-footnote">
                {t.qrFootnote}
              </p>
            )}
            <div className={`public-qr-actions ${isEmbedded ? 'public-qr-actions--embed' : ''}`} data-testid="public-qr-actions">
              <Button
                onClick={handleCopyPublicLink}
                className={`public-qr-copy ${isEmbedded ? 'public-qr-copy--embed' : ''}`}
                data-testid="public-qr-copy"
              >
                {t.copyLink}
              </Button>
              {!isEmbedded && (
                <Input
                  value={publicProjectUrl}
                  readOnly
                  className="public-qr-link"
                  data-testid="public-qr-link"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showArModal && deviceInfo.isAndroid && (
        <div className="public-ar-modal" data-testid="public-ar-modal">
          <div className="public-ar-modal-card" data-testid="public-ar-modal-card">
            {arModalType === "browser" ? (
              <>
                <p className="public-ar-modal-title" data-testid="public-ar-modal-title">
                  {t.arBestChrome}
                </p>
                <p className="public-ar-modal-text" data-testid="public-ar-modal-text">
                  {t.arBestChromeText}
                </p>
                <div className="public-ar-modal-actions" data-testid="public-ar-modal-actions">
                  <button
                    className="public-ar-modal-primary"
                    onClick={handleOpenInChrome}
                    data-testid="public-ar-modal-open-chrome"
                  >
                    {t.openInChrome}
                  </button>
                  <button
                    className="public-ar-modal-secondary"
                    onClick={async () => {
                      if (sceneViewerUrl) {
                        await navigator.clipboard.writeText(sceneViewerUrl);
                        toast.success(t.arLinkCopied);
                      }
                    }}
                    data-testid="public-ar-modal-copy"
                  >
                    {t.copyLink}
                  </button>
                  <button
                    className="public-ar-modal-tertiary"
                    onClick={() => {
                      setShowArModal(false);
                      setArModalType(null);
                    }}
                    data-testid="public-ar-modal-stay"
                  >
                    {t.closeStay3d}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="public-ar-modal-title" data-testid="public-ar-modal-title">
                  {t.arFailed}
                </p>
                <div className="public-ar-modal-actions" data-testid="public-ar-modal-actions">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.google.ar.core"
                    target="_blank"
                    rel="noreferrer"
                    className="public-ar-modal-primary"
                    data-testid="public-ar-modal-install"
                  >
                    {t.installUpdate}
                  </a>
                  <button
                    className="public-ar-modal-secondary"
                    onClick={() => {
                      setShowArModal(false);
                      setArLaunchRequested(false);
                      setArSessionLikelyOpened(false);
                      setArAttemptStartedAt(null);
                      setArFallbackHandled(false);
                      setArModalType(null);
                    }}
                    data-testid="public-ar-modal-stay"
                  >
                    {t.closeStay3d}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* iOS Quick Look modal for embed */}
      {showArModal && arModalType === "ios-quicklook" && (
        <div className="public-ar-modal" data-testid="ios-quicklook-modal">
          <div className="public-ar-modal-card" data-testid="ios-quicklook-modal-card">
            <p className="public-ar-modal-title" data-testid="ios-quicklook-modal-title">
              {t.viewInRealityTitle}
            </p>
            <p className="public-ar-modal-text" data-testid="ios-quicklook-modal-text">
              {t.tapToLaunchCamera}
            </p>
            <div className="public-ar-modal-actions" data-testid="ios-quicklook-modal-actions">
              {/* Hidden model-viewer for Quick Look activation */}
              <model-viewer
                ref={iosQuickLookRef}
                src={project?.glb_url}
                ios-src={project?.usdz_url}
                ar
                ar-modes="quick-look"
                style={{ 
                  position: "absolute",
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: "none"
                }}
              />
              <button
                className="public-ar-modal-primary"
                onClick={() => {
                  // ONLY launch Quick Look. Audio was started by the first CTA
                  // and must stay completely untouched here.
                  console.info("[AUDIO iOS] 'View in AR' (modal) - launch Quick Look, audio untouched");
                  iosQuickLookLaunchedRef.current = true;
                  if (iosQuickLookRef.current?.activateAR) {
                    iosQuickLookRef.current.activateAR();
                  }
                  setShowArModal(false);
                  setArModalType(null);
                }}
                data-testid="ios-quicklook-modal-launch"
              >
                {t.viewInAr}
              </button>
              <button
                className="public-ar-modal-tertiary"
                onClick={() => {
                  // Modal closed WITHOUT entering AR - stop the audio session
                  endIosArAudioSession();
                  setShowArModal(false);
                  setArModalType(null);
                }}
                data-testid="ios-quicklook-modal-cancel"
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {debugEnabled && (
        <div
          className="fixed bottom-6 right-6 z-[1000000] max-w-xs space-y-2 rounded-xl border border-border/60 bg-white/95 p-4 text-xs shadow-[0_12px_40px_rgba(0,0,0,0.12)]"
          data-testid="debug-panel"
        >
          <p className="font-semibold" data-testid="debug-title">
            Debug panel
          </p>
          <p data-testid="debug-user-agent">UA: {deviceInfo.userAgent || "-"}</p>
          <p data-testid="debug-ios">iOS: {deviceInfo.isIOS ? "tak" : "nie"}</p>
          <p data-testid="debug-android">Android: {deviceInfo.isAndroid ? "tak" : "nie"}</p>
          <p data-testid="debug-browser">Browser: {browserInfo.name || "-"}</p>
          <p data-testid="debug-ar-support">AR support: {String(arSupported)}</p>
          <p data-testid="debug-scene-viewer">Scene Viewer URL: {sceneViewerUrl || "-"}</p>
          <p data-testid="debug-glb">GLB: {project.glb_url || "-"}</p>
          <p data-testid="debug-usdz">USDZ: {project.usdz_url || "-"}</p>
          <p data-testid="debug-error">Ostatni błąd: {lastError || "-"}</p>
        </div>
      )}
    </div>
  );
};

// LaunchARPage - 1-click AR launcher (dla embedu)
// Automatycznie uruchamia odpowiedni tryb AR bez dodatkowego kliknięcia
const LaunchARPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | launching | error | browser-modal | desktop-qr
  const [project, setProject] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showWebXR, setShowWebXR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  // Device/browser detection
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  const isChrome = /Chrome/i.test(ua) && !/Edg|OPR|SamsungBrowser/i.test(ua);
  const isInApp = /FBAN|FBAV|Instagram|Messenger|Line|Snapchat|Twitter|MicroMessenger|SamsungBrowser/i.test(ua);
  const isDesktop = !isIOS && !isAndroid;
  
  const publicUrl = `${PRODUCTION_URL}/p/${id}`;
  
  // Debug mode
  const debugMode = searchParams.get("debug") === "1";
  const [debugInfo, setDebugInfo] = useState({});

  const t = getT(project?.language || "pl");

  // Handle return URL from embed - validate and store in sessionStorage + cookie
  useEffect(() => {
    const returnParam = searchParams.get("return");
    const existingSession = sessionStorage.getItem("arlens_return_url");
    const existingCookie = document.cookie.split('; ').find(c => c.startsWith('arlens_return_url='));
    const existingCookieValue = existingCookie ? decodeURIComponent(existingCookie.split('=')[1]) : null;
    
    const debug = {
      returnParam: returnParam || "(brak)",
      storedSession: existingSession || "(brak)",
      storedCookie: existingCookieValue || "(brak)",
      returnAllowed: false,
      reason: "",
      action: ""
    };
    
    if (returnParam) {
      try {
        const returnUrl = new URL(returnParam);
        // Security: block javascript:, data:, and empty URLs
        // Allow any http/https URL (external client pages)
        if (returnUrl.protocol === "http:" || returnUrl.protocol === "https:") {
          // Optionally: block return to own domain (ar.arlens.pl) - user should return to client
          const isOwnDomain = returnUrl.hostname === "ar.arlens.pl" || returnUrl.hostname === "arlens.pl";
          
          if (!isOwnDomain) {
            // Store in sessionStorage (primary)
            sessionStorage.setItem("arlens_return_url", returnParam);
            
            // Store in cookie (backup) - 1 hour expiry, SameSite=Lax
            const cookieValue = encodeURIComponent(returnParam);
            document.cookie = `arlens_return_url=${cookieValue}; max-age=3600; path=/; SameSite=Lax`;
            
            console.info("[LAUNCH] Return URL stored (session + cookie):", returnParam);
            debug.returnAllowed = true;
            debug.reason = "OK - zewnętrzny http/https";
            debug.storedSession = returnParam;
            debug.storedCookie = returnParam;
            debug.action = "Zapisano do sessionStorage i cookie";
          } else {
            console.warn("[LAUNCH] Return URL is own domain, skipping:", returnParam);
            debug.reason = "Pominięto: własna domena (ar.arlens.pl)";
            debug.action = "Nie zapisano (własna domena)";
          }
        } else {
          console.warn("[LAUNCH] Invalid return URL protocol:", returnUrl.protocol);
          debug.reason = `Odrzucono: zły protokół (${returnUrl.protocol})`;
          debug.action = "Odrzucono";
        }
      } catch (e) {
        console.warn("[LAUNCH] Invalid return URL:", returnParam, e.message);
        debug.reason = `Odrzucono: nieprawidłowy URL (${e.message})`;
        debug.action = "Odrzucono";
      }
    } else {
      debug.reason = "Brak parametru return w URL";
      debug.action = existingSession || existingCookieValue ? "Użyje istniejącego" : "Fallback do /p/:id";
    }
    
    setDebugInfo(debug);
  }, [searchParams, id]);

  useEffect(() => {
    const launch = async () => {
      try {
        // 1. Load project
        const fetchUrl = `${API}/public/projects/${id}`;
        console.info("[LAUNCH] Starting", { projectId: id, fetchUrl, API, BACKEND_URL });
        
        const response = await axios.get(fetchUrl);
        console.info("[LAUNCH] Response received", { status: response.status, hasData: !!response.data });
        
        const proj = response.data;
        const tp = getT(proj?.language || "pl");
        
        if (!proj || !proj.glb_url) {
          console.error("[LAUNCH] Project missing or no glb_url", { proj });
          setStatus("error");
          setErrorMsg(tp.projectNoModel);
          return;
        }
        
        console.info("[LAUNCH] Project loaded", { 
          name: proj.name, 
          glb_url: proj.glb_url?.substring(0, 50),
          usdz_url: proj.usdz_url?.substring(0, 50)
        });
        
        setProject(proj);
        setStatus("launching");

        // 2. Device detection
        console.info("[LaunchAR] Device detection", { isIOS, isAndroid, isChrome, isInApp, isDesktop });

        // Desktop - show QR modal
        if (isDesktop) {
          console.info("[LaunchAR] Desktop detected - showing QR modal");
          try {
            const qr = await QRCode.toDataURL(publicUrl, { width: 200, margin: 2 });
            setQrCodeUrl(qr);
          } catch (e) {
            console.warn("[LaunchAR] QR generation failed:", e);
          }
          setStatus("desktop-qr");
          return;
        }

        // iOS - Quick Look (render hidden model-viewer and activate AR immediately)
        if (isIOS) {
          console.info("[LaunchAR] iOS detected - launching Quick Look directly");
          if (proj.usdz_url) {
            setStatus("ios-quicklook");
            return;
          } else {
            setStatus("error");
            setErrorMsg(tp.noUsdzIos);
          }
          return;
        }

        // Android
        if (isAndroid) {
          // Non-Chrome or in-app browser - show modal
          if (!isChrome || isInApp) {
            console.info("[LaunchAR] Non-Chrome browser - showing modal");
            setStatus("browser-modal");
            return;
          }

          // Chrome - check WebXR support
          let webxrSupported = false;
          if (WEBXR_ENABLED && navigator.xr) {
            try {
              webxrSupported = await navigator.xr.isSessionSupported("immersive-ar");
            } catch (e) {
              console.warn("[LaunchAR] WebXR check failed:", e);
            }
          }

          console.info("[LaunchAR] WebXR check", { webxrSupported, WEBXR_ENABLED });

          const isFloorPlacement = proj.ar_placement_type !== "wall";

          if (webxrSupported && isFloorPlacement) {
            // WebXR - show WebXR viewer
            console.info("[LaunchAR] Launching WebXR");
            setShowWebXR(true);
            return;
          }

          // Scene Viewer fallback
          console.info("[LaunchAR] Launching Scene Viewer");
          const fileUrl = encodeURIComponent(proj.glb_url);
          const title = encodeURIComponent(proj.name || "Model 3D");
          const verticalPlacement = proj.ar_placement_type === "wall" ? "&enable_vertical_placement=true" : "";
          const sceneViewerUrl = `https://arvr.google.com/scene-viewer/1.0?file=${fileUrl}&mode=ar_only&title=${title}${verticalPlacement}`;
          window.location.href = sceneViewerUrl;
          return;
        }

        // Unknown - show QR as fallback
        setStatus("desktop-qr");

      } catch (err) {
        console.error("[LAUNCH] Error:", err.message, err.response?.status, err.response?.data);
        setStatus("error");
        setErrorMsg(err.response?.status === 404 
          ? getT("pl").projectNotFoundShort
          : `${getT("pl").projectLoadFailed} (${err.message})`);
      }
    };

    launch();
  }, [id, isIOS, isAndroid, isChrome, isInApp, isDesktop, publicUrl]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      console.warn("Copy failed:", e);
    }
  };

  // Handle close/return after AR - check sessionStorage first, then cookie
  const handleClose = () => {
    // 1. Try sessionStorage first (primary)
    let returnUrl = sessionStorage.getItem("arlens_return_url");
    let source = "sessionStorage";
    
    // 2. Try cookie as backup
    if (!returnUrl) {
      const cookie = document.cookie.split('; ').find(c => c.startsWith('arlens_return_url='));
      if (cookie) {
        returnUrl = decodeURIComponent(cookie.split('=')[1]);
        source = "cookie";
      }
    }
    
    console.info("[LAUNCH] handleClose called", { 
      returnUrl,
      source,
      projectId: id,
      publicUrl 
    });
    
    if (returnUrl) {
      // Clear stored values (both sessionStorage and cookie)
      sessionStorage.removeItem("arlens_return_url");
      document.cookie = "arlens_return_url=; max-age=0; path=/";
      
      console.info("[LAUNCH] Returning to client page:", returnUrl, `(from ${source})`);
      window.location.href = returnUrl;
      return;
    }
    
    // Fallback: go to public viewer page
    console.info("[LAUNCH] No return URL found, going to public viewer:", publicUrl);
    window.location.href = publicUrl;
  };

  const handleWebXRFallback = () => {
    if (project?.glb_url) {
      console.info("[LaunchARPage] Fallback to Scene Viewer", { glbUrl: project.glb_url, projectId: id });
      openSceneViewer(project.glb_url, project.name, project.ar_placement_type, id);
    }
  };

  // WebXR View
  if (showWebXR && project) {
    return (
      <WebXRViewer
        glbUrl={project.glb_url}
        projectName={project.name}
        onClose={handleClose}
        onFallbackToSceneViewer={handleWebXRFallback}
        allowUserScaling={project.allow_user_scaling !== false}
        debugMode={debugMode}
        language={project.language || "pl"}
        audioUrl={project.audio_url}
        audioPlaybackMode={project.audio_playback_mode}
      />
    );
  }

  // Desktop QR Modal
  if (status === "desktop-qr") {
    return (
      <div className="public-qr-modal" onClick={handleClose}>
        <div className="public-qr-card" onClick={(e) => e.stopPropagation()}>
          <button className="public-qr-close" onClick={handleClose} aria-label={t.close}>×</button>
          <h3 className="public-qr-title">{t.seeModelInSpace}</h3>
          {qrCodeUrl ? (
            <img src={qrCodeUrl} alt="QR Code" className="public-qr-image" />
          ) : (
            <div className="public-qr-image public-qr-placeholder">{t.generatingQr}</div>
          )}
          <ol className="public-qr-steps">
            <li>{t.qrStep1}</li>
            <li>{t.qrStep2}</li>
            <li>{t.qrStep3}</li>
            <li>{t.qrStep4}</li>
          </ol>
          <p className="public-qr-footnote">
            {t.qrFootnote}
          </p>
          <div className="public-qr-actions">
            <Button onClick={handleCopyLink} className="public-qr-copy">
              {linkCopied ? t.copied : t.copyLink}
            </Button>
            <Input value={publicUrl} readOnly className="public-qr-link" />
          </div>
        </div>
      </div>
    );
  }

  // Browser modal (non-Chrome)
  if (status === "browser-modal") {
    return (
      <div className="public-ar-modal">
        <div className="public-ar-modal-card">
          <p className="public-ar-modal-title">{t.arBestChrome}</p>
          <p className="public-ar-modal-text">
            {t.arBestChromeText}
          </p>
          <div className="public-ar-modal-actions">
            <button className="public-ar-modal-primary" onClick={handleCopyLink}>
              {linkCopied ? t.copied : t.copyLink}
            </button>
            <button className="public-ar-modal-tertiary" onClick={handleClose}>
              {t.close}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Quick Look - render hidden model-viewer and auto-activate AR
  if (status === "ios-quicklook" && project?.usdz_url) {
    return (
      <div className="webxr-fullscreen" style={{ background: "#000" }}>
        {/* Hidden model-viewer that will trigger Quick Look */}
        <model-viewer
          ref={(el) => {
            if (el && !el._arActivated) {
              el._arActivated = true;
              // Wait for model-viewer to be ready then activate AR
              const tryActivateAR = () => {
                if (el.activateAR) {
                  console.info("[LaunchAR] Activating Quick Look");
                  el.activateAR();
                } else {
                  setTimeout(tryActivateAR, 100);
                }
              };
              // Small delay to ensure model-viewer is initialized
              setTimeout(tryActivateAR, 300);
            }
          }}
          src={project.glb_url}
          ios-src={project.usdz_url}
          ar
          ar-modes="quick-look"
          style={{ 
            width: "100%", 
            height: "100%", 
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: "none"
          }}
        />
        {/* Loading indicator while Quick Look opens */}
        <div className="webxr-onboarding" style={{ position: "relative", zIndex: 1 }}>
          <div className="webxr-loading-spinner">
            <div style={{ 
              width: 48, 
              height: 48, 
              border: "3px solid #333", 
              borderTopColor: "#fff", 
              borderRadius: "50%", 
              animation: "spin 1s linear infinite" 
            }} />
          </div>
          <p style={{ color: "#fff", marginTop: 16 }}>{t.launchingAr}</p>
          <button 
            onClick={handleClose} 
            className="webxr-link" 
            style={{ marginTop: 24, color: "#888" }}
          >
            {t.cancel}
          </button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Error
  if (status === "error") {
    return (
      <div className="webxr-fullscreen">
        <div className="webxr-onboarding">
          <h2 className="webxr-title" style={{ color: "#ef4444" }}>{t.openProjectFailed}</h2>
          <p className="webxr-subtitle" style={{ marginBottom: 24 }}>{errorMsg}</p>
          <div className="webxr-buttons">
            <Button 
              onClick={() => window.location.reload()} 
              className="webxr-start-btn"
            >
              {t.tryAgain}
            </Button>
            <button 
              onClick={() => window.location.href = `/p/${id}`} 
              className="webxr-link"
            >
              {t.open3dPreview}
            </button>
            {isAndroid && (
              <button 
                onClick={() => {
                  // Try Scene Viewer as last resort
                  const defaultGlbUrl = `https://files.ar.arlens.pl/projects/${id}_model.glb`;
                  openSceneViewer(defaultGlbUrl, "Model 3D", "floor", id);
                }} 
                className="webxr-link"
                style={{ marginTop: 8 }}
              >
                {t.useStandardAr}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Loading / Launching
  return (
    <div className="webxr-fullscreen">
      <div className="webxr-onboarding">
        <div className="webxr-loading-spinner">
          <div style={{ 
            width: 48, 
            height: 48, 
            border: "3px solid #e5e5e5", 
            borderTopColor: "#3b82f6", 
            borderRadius: "50%", 
            animation: "spin 1s linear infinite" 
          }} />
        </div>
        <p style={{ color: "#71717a", marginTop: 16 }}>
          {status === "loading" ? t.loading : t.launchingAr}
        </p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
      
      {/* Debug panel */}
      {debugMode && (
        <div style={{
          position: "fixed",
          bottom: 10,
          left: 10,
          right: 10,
          background: "rgba(0,0,0,0.95)",
          color: "#0f0",
          padding: 12,
          borderRadius: 8,
          fontSize: 11,
          fontFamily: "monospace",
          zIndex: 9999,
          maxHeight: "50vh",
          overflow: "auto"
        }}>
          <div style={{ fontWeight: "bold", marginBottom: 8, color: "#fff" }}>🔧 Debug: Return URL Flow</div>
          <div><b>returnParam (z URL):</b> <span style={{color: debugInfo.returnParam !== "(brak)" ? "#0f0" : "#f55"}}>{debugInfo.returnParam}</span></div>
          <div><b>storedSession:</b> <span style={{color: debugInfo.storedSession !== "(brak)" ? "#0f0" : "#888"}}>{debugInfo.storedSession}</span></div>
          <div><b>storedCookie:</b> <span style={{color: debugInfo.storedCookie !== "(brak)" ? "#0f0" : "#888"}}>{debugInfo.storedCookie}</span></div>
          <div><b>returnAllowed:</b> {debugInfo.returnAllowed ? "✅ TAK" : "❌ NIE"}</div>
          <div><b>reason:</b> {debugInfo.reason}</div>
          <div><b>action:</b> {debugInfo.action}</div>
          <div style={{ marginTop: 8, borderTop: "1px solid #333", paddingTop: 8 }}>
            <div><b>projectId:</b> {id}</div>
            <div><b>status:</b> {status}</div>
            <div><b>device:</b> {isIOS ? "iOS" : isAndroid ? "Android" : "Desktop"} {isChrome ? "(Chrome)" : ""}</div>
            <div><b>publicUrl (fallback):</b> {publicUrl}</div>
          </div>
          <div style={{ marginTop: 8, borderTop: "1px solid #333", paddingTop: 8, color: "#ff0" }}>
            <b>Po "Zakończ AR":</b> {debugInfo.storedSession || debugInfo.storedCookie ? `→ ${debugInfo.storedSession || debugInfo.storedCookie}` : `→ ${publicUrl} (fallback)`}
          </div>
        </div>
      )}
    </div>
  );
};

// WebXRPage - dedykowana strona do uruchamiania WebXR (top-level, nie w iframe)
const WebXRPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const debugMode = searchParams.get("debug") === "1";

  const t = getT(project?.language || "pl");

  useEffect(() => {
    const loadProject = async () => {
      try {
        const response = await axios.get(`${API}/public/projects/${id}`);
        setProject(response.data);
      } catch (err) {
        console.error("[WebXRPage] Load error:", err);
        setError(getT("pl").projectLoadFailed);
      } finally {
        setLoading(false);
      }
    };
    loadProject();
  }, [id]);

  const handleClose = () => {
    // Go back or close
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.close();
    }
  };

  const handleFallback = () => {
    // Fallback to Scene Viewer
    if (project?.glb_url) {
      console.info("[WebXRPage] Fallback to Scene Viewer", { glbUrl: project.glb_url, projectId: id });
      openSceneViewer(project.glb_url, project.name, project.ar_placement_type, id);
    }
  };

  if (loading) {
    return (
      <div className="webxr-fullscreen">
        <div className="webxr-onboarding">
          <div className="webxr-loading-spinner">
            <div style={{ width: 48, height: 48, border: "3px solid #e5e5e5", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
          </div>
          <p style={{ color: "#71717a", marginTop: 16 }}>{t.loadingProject}</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="webxr-fullscreen">
        <div className="webxr-onboarding">
          <h2 className="webxr-title">{t.error}</h2>
          <p className="webxr-subtitle">{error || t.projectNotFoundShort}</p>
          <button onClick={handleClose} className="webxr-link">{t.close}</button>
        </div>
      </div>
    );
  }

  return (
    <WebXRViewer
      glbUrl={project.glb_url}
      projectName={project.name}
      onClose={handleClose}
      onFallbackToSceneViewer={handleFallback}
      allowUserScaling={project.allow_user_scaling !== false}
      debugMode={debugMode}
    />
  );
};

// LaunchPage - pośrednik do uruchamiania Scene Viewer z poziomu embed
const LaunchPage = () => {
  const { id } = useParams();
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [lang, setLang] = useState("pl");

  const t = getT(lang);

  useEffect(() => {
    const launchAR = async () => {
      try {
        const response = await axios.get(`${API}/public/projects/${id}`);
        const project = response.data;
        const tp = getT(project?.language || "pl");
        setLang(project?.language || "pl");
        
        if (!project || !project.glb_url) {
          setStatus("error");
          setErrorMsg(tp.noModel3d);
          return;
        }

        const glbUrl = project.glb_url;
        
        // Validate URL - must be absolute HTTPS
        if (!glbUrl.startsWith("https://")) {
          console.error("[LAUNCH_AR] Invalid GLB URL:", glbUrl);
          setStatus("error");
          setErrorMsg(tp.invalidModelUrl);
          return;
        }

        console.info("[LAUNCH_AR] Opening Scene Viewer", { glbUrl, projectId: id });
        
        // Przekierowanie do Scene Viewer
        setStatus("redirecting");
        openSceneViewer(glbUrl, project.name, project.ar_placement_type, id);
      } catch (error) {
        console.error("[LAUNCH_AR_ERROR]", error);
        setStatus("error");
        setErrorMsg(getT("pl").arLaunchFailed);
      }
    };

    launchAR();
  }, [id]);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      background: "#fafafa",
      fontFamily: "system-ui, sans-serif",
      padding: "20px",
      textAlign: "center"
    }}>
      {status === "loading" && (
        <>
          <div style={{
            width: "40px",
            height: "40px",
            border: "3px solid #e5e5e5",
            borderTopColor: "#3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }} />
          <p style={{ marginTop: "16px", color: "#525252" }}>{t.loadingArModel}</p>
        </>
      )}
      {status === "redirecting" && (
        <p style={{ color: "#525252" }}>{t.launchingAr}</p>
      )}
      {status === "error" && (
        <>
          <p style={{ color: "#ef4444", fontWeight: 500 }}>{errorMsg}</p>
          <button
            onClick={() => window.close()}
            style={{
              marginTop: "16px",
              padding: "10px 20px",
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer"
            }}
          >
            {t.close}
          </button>
        </>
      )}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// FACE AR POC - ISOLATED COMPONENTS
// These are separate from standard AR flows (WebXR, Scene Viewer, iOS Quick Look)
// ============================================================================

// Face AR POC Viewer Page
const FaceARPocViewerPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const debugMode = searchParams.get("debug") === "1";

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/projects/${id}`);
        setProject(response.data);
      } catch (err) {
        console.error("Failed to load project:", err);
        setError("Nie udało się załadować projektu");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#000" }}>
        <div style={{ color: "#fff" }}>Ładowanie...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#000" }}>
        <div style={{ color: "#f55", textAlign: "center" }}>
          <div>{error || "Projekt nie znaleziony"}</div>
          <button onClick={() => navigate("/")} style={{ marginTop: 16, padding: "8px 16px", background: "#333", color: "#fff", border: "none", borderRadius: 8 }}>
            Wróć
          </button>
        </div>
      </div>
    );
  }

  return (
    <FaceARViewer
      glbUrl={project.glb_url}
      projectName={project.name}
      faceArTransform={project.face_ar_transform}
      onClose={() => navigate(`/p/${id}`)}
      debugMode={debugMode}
    />
  );
};

// New Face AR Project Page
const NewFaceARProjectPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [productType, setProductType] = useState("glasses");
  const [glbFile, setGlbFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Nazwa projektu jest wymagana");
      return;
    }
    
    if (!glbFile) {
      toast.error("Plik GLB jest wymagany");
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("description", description);
      formData.append("project_type", "face_ar");
      formData.append("face_ar_product_type", productType);
      formData.append("glb", glbFile);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/projects`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      toast.success("Projekt Face AR utworzony!");
      // Redirect to Face AR editor
      navigate(`/poc/face-ar-editor/${response.data.id}`);
    } catch (err) {
      console.error("Failed to create project:", err);
      toast.error(err.response?.data?.detail || "Błąd tworzenia projektu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container max-w-2xl py-8" data-testid="new-facear-project-page">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Nowy projekt Face AR</CardTitle>
            <Badge variant="secondary">beta</Badge>
          </div>
          <CardDescription>
            Stwórz projekt do przymierzania wirtualnego (okulary, czapki, maski).
            Po utworzeniu zostaniesz przekierowany do edytora pozycji.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Nazwa projektu *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="np. Okulary Ray-Ban Aviator"
                required
                data-testid="facear-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Opis (opcjonalny)</label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="np. Klasyczne okulary przeciwsłoneczne"
                data-testid="facear-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Typ produktu</label>
              <div className="flex flex-wrap gap-2">
                {["glasses", "hat", "mask", "custom"].map((type) => (
                  <Button
                    key={type}
                    type="button"
                    variant={productType === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setProductType(type)}
                    className="capitalize"
                    data-testid={`facear-type-${type}`}
                  >
                    {type === "glasses" && "Okulary"}
                    {type === "hat" && "Czapka"}
                    {type === "mask" && "Maska"}
                    {type === "custom" && "Inne"}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Typ produktu określa domyślną pozycję modelu na głowie
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Model 3D (GLB) *</label>
              <Input
                type="file"
                accept=".glb"
                onChange={(e) => setGlbFile(e.target.files?.[0] || null)}
                required
                data-testid="facear-glb-input"
              />
              <p className="text-xs text-muted-foreground">
                Plik GLB z modelem produktu (max 100MB)
              </p>
            </div>
            
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard")}
                data-testid="facear-cancel-btn"
              >
                Anuluj
              </Button>
              <Button
                type="submit"
                disabled={loading || !name.trim() || !glbFile}
                data-testid="facear-submit-btn"
              >
                {loading ? "Tworzenie..." : "Utwórz i przejdź do edytora"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Face AR POC Editor Page
const FaceARPocEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const auth = useAuth();

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/projects/${id}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
        setProject(response.data);
      } catch (err) {
        console.error("Failed to load project:", err);
        setError("Nie udało się załadować projektu");
      } finally {
        setLoading(false);
      }
    };
    
    if (auth.token) {
      fetchProject();
    } else {
      setError("Musisz być zalogowany");
      setLoading(false);
    }
  }, [id, auth.token]);

  const handleSave = async (transform) => {
    try {
      setSaving(true);
      await axios.put(
        `${BACKEND_URL}/api/projects/${id}`,
        { face_ar_transform: transform },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success("Zapisano ustawienia Face AR");
      navigate(`/projects/${id}`);
    } catch (err) {
      console.error("Failed to save:", err);
      toast.error("Nie udało się zapisać");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#1a1a2e" }}>
        <div style={{ color: "#fff" }}>Ładowanie...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#1a1a2e" }}>
        <div style={{ color: "#f55", textAlign: "center" }}>
          <div>{error || "Projekt nie znaleziony"}</div>
          <button onClick={() => navigate("/dashboard")} style={{ marginTop: 16, padding: "8px 16px", background: "#333", color: "#fff", border: "none", borderRadius: 8 }}>
            Wróć do panelu
          </button>
        </div>
      </div>
    );
  }

  return (
    <FaceAREditor
      glbUrl={project.glb_url}
      projectName={project.name}
      projectId={id}
      initialTransform={project.face_ar_transform}
      productType={project.face_ar_product_type || "glasses"}
      onSave={handleSave}
      onCancel={() => navigate("/dashboard")}
    />
  );
};

function App() {
  const auth = useAuth();

  const handleLogout = () => {
    auth.logout();
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<LoginPage onLogin={auth.login} />} />
          <Route
            path="/"
            element={<Navigate to={auth.token ? "/dashboard" : "/login"} replace />}
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute token={auth.token}>
                <Shell>
                  <Sidebar onLogout={handleLogout} />
                  <DashboardPage />
                </Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute token={auth.token}>
                <Shell>
                  <Sidebar onLogout={handleLogout} />
                  <NewProjectPage />
                </Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new-face-ar"
            element={
              <ProtectedRoute token={auth.token}>
                <Shell>
                  <Sidebar onLogout={handleLogout} />
                  <NewFaceARProjectPage />
                </Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute token={auth.token}>
                <Shell>
                  <Sidebar onLogout={handleLogout} />
                  <ProjectDetailPage />
                </Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <ProtectedRoute token={auth.token}>
                <Shell>
                  <Sidebar onLogout={handleLogout} />
                  <EditProjectPage />
                </Shell>
              </ProtectedRoute>
            }
          />
          <Route path="/p/:id" element={<PublicViewerPage />} />
          <Route path="/launch-ar/:id" element={<ARErrorBoundary><LaunchARPage /></ARErrorBoundary>} />
          <Route path="/webxr/:id" element={<ARErrorBoundary><WebXRPage /></ARErrorBoundary>} />
          <Route path="/launch/:id" element={<ARErrorBoundary><LaunchPage /></ARErrorBoundary>} />
          
          {/* Face AR POC Routes - isolated from standard AR */}
          <Route path="/poc/face-ar/:id" element={<FaceARPocViewerPage />} />
          <Route 
            path="/poc/face-ar-editor/:id" 
            element={
              <ProtectedRoute token={auth.token}>
                <FaceARPocEditorPage />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
